import React, { useState, useEffect, Suspense, useRef, useCallback, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import '../themes.css';
import '../core-pages.css';
import { Turnstile } from '@marsidev/react-turnstile';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence, useMotionValue, useSpring, useReducedMotion } from 'framer-motion';
import { toast } from '../lib/feedbackToast';
import { promptAction, confirmAction } from '../lib/actionDialog';
import { cn } from '../lib/utils';
import {
  ExternalLink, Sparkles, Cloud, MessageCircle,
  Bell, Share2, CheckCircle, X, Play, ChevronRight, Flag, MapPin, QrCode, ShieldCheck, Copy, Mail, Download, AlertCircle, Check, CreditCard, Lock
} from 'lucide-react';
import { PLATFORMS, detectPlatformFromUrl } from '../components/SocialIconsBlock';
import { loadGoogleFont } from '../lib/fontLoader';

const InstagramIcon = ({ size = 24, className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size} className={className}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);
// Leaflet removed — unused import that was adding ~200KB to the bundle

import { QRCodeSVG } from 'qrcode.react';
import MagneticTether from '../components/MagneticTether';
import CheckoutModal from '../components/CheckoutModal';
import ProfileFooter from '../components/ProfileFooter';
import VerifiedBadge from '../components/VerifiedBadge';
const SafetyNumberModal = React.lazy(() => import('../components/SafetyNumberModal'));
import { readInjectedPublicProfile, readPublicProfileCache, writePublicProfileCache } from '../lib/profileCache';
import { fetchPublicProfile, fetchPublicProfileVersion } from '../lib/publicProfileApi';
import { readLatestLiveProfileUpdate, subscribeToLiveProfileUpdates } from '../lib/liveProfilePreview';
import { DEFAULT_CURRENCY, formatMoney, normalizeCurrency, splitPriceCurrency } from '../lib/currency';
import { appendAnalyticsQuery, buildAnalyticsPayload, captureAnalyticsContext, detectAnalyticsPlatform } from '../lib/analyticsAttribution';
import { BloomFilter } from '../lib/bloomFilter';
import ContentCredentialBadge from '../components/ContentCredentialBadge';
import { resolveText, getRobustFontStack } from '../lib/i18n';

import { BrutalCard, BrutalButton, BrutalInput } from '../components/BrutalUI';
import LiveCanvas from '../components/LiveCanvas';
import { useSpatialTracking } from '../hooks/useSpatialTracking';
import GenerativeCanvas from '../components/GenerativeCanvas';
import { DecorationLayer } from '../components/DecorationCanvas';

const FluidCanvas = React.lazy(() => import('../components/FluidCanvas'));
const SubscriptionModal = React.lazy(() => import('../components/SubscriptionModal'));
const ContactModal = React.lazy(() => import('../components/ContactModal'));
const TermsPortalModal = React.lazy(() => import('../components/TermsPortalModal'));

const getSupabaseClient = () => import('../lib/supabase').then((module) => module.supabase);

const getProfileCurrency = (profile) => normalizeCurrency(profile?.tip_jar?.currency_code || profile?.tip_jar?.currency_symbol || profile?.currency_symbol, DEFAULT_CURRENCY);
const getSubscriberEmailKey = (profileId) => `linkit_sub_email_${profileId}`;
const getSubscriberTokenKey = (profileId) => `linkit_sub_token_${profileId}`;
const getSubscriberFlagKey = (profileId) => `linkit_sub_${profileId}`;
const LOCATION_CONSENT_VERSION = 'v1';
const LOCATION_FAILED_RETRY_MS = 10 * 60 * 1000;
const getLocationConsentKey = (profileId) => `linkit_location_consent_${LOCATION_CONSENT_VERSION}_${profileId}`;

const readLocationConsentChoice = (profileId) => {
  if (typeof window === 'undefined' || !profileId) return '';
  try {
    return localStorage.getItem(getLocationConsentKey(profileId)) || '';
  } catch {
    return '';
  }
};

const storeLocationConsentChoice = (profileId, choice) => {
  if (typeof window === 'undefined' || !profileId) return;
  try {
    const value = choice === 'failed' ? `failed:${Date.now()}` : choice;
    localStorage.setItem(getLocationConsentKey(profileId), value);
  } catch {
    // Storage can be blocked; the browser permission prompt is still authoritative.
  }
};

const shouldSkipLocationPrompt = (profileId) => {
  const choice = readLocationConsentChoice(profileId);
  if (choice === 'accepted' || choice === 'denied' || choice === 'dismissed') return true;
  if (!choice.startsWith('failed:')) return false;
  const failedAt = Number(choice.slice('failed:'.length));
  return Number.isFinite(failedAt) && Date.now() - failedAt < LOCATION_FAILED_RETRY_MS;
};

const readBrowserPosition = (options) => new Promise((resolve, reject) => {
  navigator.geolocation.getCurrentPosition(resolve, reject, options);
});

const requestGeolocationWithFallback = async () => {
  try {
    return await readBrowserPosition({
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 30000,
    });
  } catch (error) {
    if (error?.code !== 2 && error?.code !== 3) throw error;
    return readBrowserPosition({
      enableHighAccuracy: false,
      timeout: 20000,
      maximumAge: 10 * 60 * 1000,
    });
  }
};

const readSubscriberAccess = (profileId) => {
  if (typeof window === 'undefined' || !profileId) return { email: '', token: '' };
  return {
    email: localStorage.getItem(getSubscriberEmailKey(profileId)) || '',
    token: localStorage.getItem(getSubscriberTokenKey(profileId)) || '',
  };
};

const storeSubscriberAccess = (profileId, email, token) => {
  if (typeof window === 'undefined' || !profileId || !email || !token) return;
  localStorage.setItem(getSubscriberFlagKey(profileId), 'true');
  localStorage.setItem(getSubscriberEmailKey(profileId), email);
  localStorage.setItem(getSubscriberTokenKey(profileId), token);
};

const clearSubscriberAccess = (profileId) => {
  if (typeof window === 'undefined' || !profileId) return;
  localStorage.removeItem(getSubscriberFlagKey(profileId));
  localStorage.removeItem(getSubscriberEmailKey(profileId));
  localStorage.removeItem(getSubscriberTokenKey(profileId));
};

const formatItemPrice = (price, currency, fallback) => {
  if (!price) return '';
  const parsed = splitPriceCurrency(price, normalizeCurrency(currency, fallback));
  return formatMoney(parsed.price || price, parsed.currency, fallback);
};

const seededUnit = (seed) => {
  const value = Math.sin(seed * 9999) * 10000;
  return value - Math.floor(value);
};

const sanitizeUrl = (url) => {
  if (!url) return '#';
  const sUrl = String(url).trim();
  try {
    const parsed = new URL(sUrl, 'https://fallback.com');
    if (['javascript:', 'data:', 'vbscript:', 'file:'].includes(parsed.protocol)) return '#';
  } catch {
    if (/^(javascript|data|vbscript|file):/i.test(sUrl)) return '#';
  }
  return sUrl;
};

const sanitizeAbsoluteAssetUrl = (url) => {
  const sUrl = sanitizeUrl(url);
  if (!sUrl || sUrl === '#') return '';

  try {
    const parsed = new URL(sUrl);
    if (['https:', 'data:'].includes(parsed.protocol)) return parsed.href;
  } catch {
    return '';
  }

  return '';
};

const sanitizeCalendlyUrl = (url) => {
  try {
    const parsed = new URL(String(url || '').trim());
    const host = parsed.hostname.toLowerCase();
    const isAllowedHost = host === 'calendly.com' || host.endsWith('.calendly.com');
    return parsed.protocol === 'https:' && isAllowedHost ? parsed.href : '';
  } catch {
    return '';
  }
};

const canUseAttributionRoute = (link) => {
  if (!link?.attribution_enabled || !link?.id || link?.is_gated) return false;
  try {
    const parsed = new URL(String(link.url || '').trim());
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const getPublicLinkHref = (link) => {
  if (canUseAttributionRoute(link)) {
    return appendAnalyticsQuery(`/api/v1/go?link_id=${encodeURIComponent(link.id)}`);
  }

  let targetUrl = link?.url;
  if (typeof navigator !== 'undefined' && link?.advanced_features?.routing?.enabled) {
    const ua = navigator.userAgent || navigator.vendor || window?.opera || '';
    if (/iPad|iPhone|iPod/.test(ua) && !window?.MSStream && link.advanced_features.routing.ios_url) {
      targetUrl = link.advanced_features.routing.ios_url;
    } else if (/android/i.test(ua) && link.advanced_features.routing.android_url) {
      targetUrl = link.advanced_features.routing.android_url;
    }
  }
  return sanitizeUrl(targetUrl);
};

const sanitizeFontFamilyName = (value) => (
  String(value || 'Linkit Custom')
    .replace(/[^a-zA-Z0-9 _-]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 64) || 'Linkit Custom'
);

const cssString = (value) => String(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const HEX_RE = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;

const hexToRgb = (value) => {
  const match = String(value || '').trim().match(HEX_RE);
  if (!match) return null;
  return {
    r: parseInt(match[1], 16),
    g: parseInt(match[2], 16),
    b: parseInt(match[3], 16),
  };
};

const relativeLuminance = (rgb) => {
  if (!rgb) return 0;
  const transform = (channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
  };
  return (0.2126 * transform(rgb.r)) + (0.7152 * transform(rgb.g)) + (0.0722 * transform(rgb.b));
};

const contrastRatio = (first, second) => {
  const firstLuminance = relativeLuminance(hexToRgb(first));
  const secondLuminance = relativeLuminance(hexToRgb(second));
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);
  return (lighter + 0.05) / (darker + 0.05);
};

const normalizeHexColor = (value) => {
  const match = String(value || '').trim().match(HEX_RE);
  return match ? `#${match.slice(1).join('').toUpperCase()}` : null;
};

const hexToRgba = (value, alpha = 1) => {
  const rgb = hexToRgb(value);
  if (!rgb) return `rgba(15, 23, 42, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

const getProfileQrTheme = (profile) => {
  const background = '#FFFFFF';
  const accent = normalizeHexColor(profile?.accent_color || profile?.button_color || profile?.title_color) || '#0F172A';
  const foregroundCandidates = [
    profile?.accent_color,
    profile?.title_color,
    profile?.button_color,
    profile?.link_text_color,
    profile?.bio_color,
  ].map(normalizeHexColor).filter(Boolean);
  const foreground = foregroundCandidates.find(color => contrastRatio(color, background) >= 7) || '#0F172A';

  return {
    accent,
    background,
    foreground,
    shadow: hexToRgba(accent, 0.25),
  };
};

const getDarkProfileFieldColor = (profile) => {
  const palette = [
    profile?.title_color,
    profile?.bio_color,
    profile?.button_color,
    profile?.accent_color,
    profile?.link_text_color,
    profile?.card_bg,
    profile?.bg_color,
  ].map(normalizeHexColor).filter(Boolean);

  if (!palette.length) {
    return '#0F172A';
  }

  const darkest = palette.reduce((currentDarkest, color) => (
    relativeLuminance(hexToRgb(color)) < relativeLuminance(hexToRgb(currentDarkest)) ? color : currentDarkest
  ), palette[0]);

  return relativeLuminance(hexToRgb(darkest)) < 0.42 ? darkest : '#0F172A';
};

const pickVisibleRingColor = (profile) => {
  const surfaceColor = profile?.card_bg || profile?.bg_color || '#ffffff';
  const accentColor = profile?.accent_color || profile?.button_color || profile?.title_color;

  if (accentColor && contrastRatio(accentColor, surfaceColor) >= 2.2) {
    return accentColor;
  }

  return relativeLuminance(hexToRgb(surfaceColor)) > 0.45 ? '#111827' : '#FFF4E6';
};

const pickContrastStroke = (color, light = '#FFF4F2', dark = '#0f172a') => (
  relativeLuminance(hexToRgb(color)) > 0.45 ? dark : light
);

const getProfileAccentColor = (profile, fallback = '#2563eb') => (
  profile?.accent_color || profile?.button_color || profile?.title_color || fallback
);

const getProfileFontMood = (profile) => {
  const font = String(profile?.title_font_family || profile?.font_family || '').toLowerCase();
  const titleWeight = Number(profile?.title_font_weight || profile?.font_weight || 0);

  if (/mono|code|console|jetbrains|fira|inconsolata|source code/.test(font)) return 'mono';
  if (/serif|playfair|cormorant|garamond|bodoni|libre baskerville|lora/.test(font)) return 'serif';
  if (/round|nunito|quicksand|rubik|comfortaa|baloo|fredoka/.test(font)) return 'soft';
  if (titleWeight >= 800 || /black|display|archivo|clash|anton|bebas|impact|syne|grotesk/.test(font)) return 'bold';
  return 'minimal';
};

const resolveSocialIconStyle = (profile) => {
  const style = profile?.social_icon_style || 'mono';
  if (style !== 'match') return style;

  const mood = getProfileFontMood(profile);
  if (mood === 'serif') return 'outline';
  if (mood === 'mono') return 'square';
  if (mood === 'soft') return 'soft';
  if (mood === 'bold') return 'filled';
  return 'mono';
};

const getSocialIconProps = ({ profile, platform, iconColor, size = 40 }) => {
  const socialStyle = resolveSocialIconStyle(profile);
  const accent = iconColor;
  const readableOnAccent = pickContrastStroke(accent, '#FFFFFF', '#0F172A');
  const baseClass = 'flex items-center justify-center transition-all hover:scale-110 active:scale-95 will-change-transform';
  const containedSize = { width: size, height: size };

  if (socialStyle === 'brand') {
    return {
      wrapperClassName: 'gap-4',
      className: cn(baseClass, 'rounded-2xl text-white hover:shadow-xl', platform.brandBorder && 'border'),
      style: {
        ...containedSize,
        background: platform.brandBackground || platform.gradient || platform.color,
        borderColor: platform.brandBorder,
      },
    };
  }

  if (socialStyle === 'outline') {
    return {
      wrapperClassName: 'gap-4',
      className: cn(baseClass, 'rounded-full hover:opacity-80'),
      style: { ...containedSize, border: `1.5px solid ${accent}`, color: accent },
    };
  }

  if (socialStyle === 'filled') {
    return {
      wrapperClassName: 'gap-4',
      className: cn(baseClass, 'rounded-full shadow-lg hover:shadow-xl'),
      style: { ...containedSize, backgroundColor: accent, color: readableOnAccent },
    };
  }

  if (socialStyle === 'soft') {
    return {
      wrapperClassName: 'gap-3',
      className: cn(baseClass, 'rounded-full border hover:shadow-md'),
      style: {
        ...containedSize,
        backgroundColor: hexToRgba(accent, 0.12),
        borderColor: hexToRgba(accent, 0.18),
        color: accent,
      },
    };
  }

  if (socialStyle === 'glass') {
    return {
      wrapperClassName: 'gap-3',
      className: cn(baseClass, 'rounded-2xl border shadow-lg'),
      style: {
        ...containedSize,
        backgroundColor: 'rgba(255,255,255,0.18)',
        borderColor: 'rgba(255,255,255,0.28)',
        color: iconColor || accent,
        backdropFilter: 'blur(14px)',
        boxShadow: `0 14px 36px -24px ${hexToRgba(accent, 0.8)}`,
      },
    };
  }

  if (socialStyle === 'square') {
    return {
      wrapperClassName: 'gap-3',
      className: cn(baseClass, 'rounded-xl border'),
      style: {
        ...containedSize,
        backgroundColor: hexToRgba(accent, 0.1),
        borderColor: hexToRgba(accent, 0.2),
        color: accent,
      },
    };
  }

  return {
    wrapperClassName: 'gap-4',
    className: cn(baseClass, 'hover:opacity-70'),
    style: { color: accent, width: Math.max(26, size - 14), height: Math.max(26, size - 14) },
  };
};

const getSocialIconNode = (platform, profile) => (
  resolveSocialIconStyle(profile) === 'brand' && platform.brandSvg ? platform.brandSvg : platform.svg
);

/* ============================================================
   PLATFORM LOGO SVGS
   ============================================================ */
const SpotifyLogo = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
  </svg>
);
const AppleMusicLogo = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M23.997 6.124c0-.738-.065-1.47-.24-2.19-.317-1.31-1.062-2.31-2.18-3.043C21.003.517 20.373.285 19.7.164c-.517-.093-1.038-.135-1.564-.15-.04-.003-.083-.01-.124-.013H5.988c-.152.01-.303.017-.455.026C4.786.07 4.043.15 3.34.428 2.004.958 1.04 1.88.475 3.208c-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.802.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03c.525 0 1.048-.034 1.57-.1.823-.106 1.597-.35 2.296-.81.84-.553 1.472-1.287 1.88-2.208.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.045-1.773-.6-1.943-1.536-.142-.773.227-1.624 1.038-2.022.323-.16.67-.25 1.018-.324.378-.082.758-.153 1.134-.24.274-.063.457-.23.51-.516.014-.063.02-.13.02-.193 0-1.815 0-3.63-.002-5.443 0-.062-.01-.125-.026-.185-.04-.15-.15-.243-.304-.234-.16.01-.318.035-.475.066-.76.15-1.52.303-2.28.456l-2.326.47-1.374.278c-.016.003-.032.01-.048.013-.277.077-.377.203-.39.49-.002.042 0 .086 0 .13-.002 2.602 0 5.204-.003 7.805 0 .42-.047.836-.215 1.227-.278.64-.77 1.04-1.434 1.233-.35.1-.71.16-1.075.172-.96.036-1.755-.6-1.92-1.544-.14-.812.23-1.685 1.154-2.075.357-.15.73-.232 1.108-.31.287-.06.575-.116.86-.177.383-.083.583-.323.6-.714v-.15c0-2.96 0-5.922.002-8.882 0-.123.013-.25.042-.37.07-.285.273-.448.546-.518.255-.066.515-.112.774-.165.733-.15 1.466-.296 2.2-.444l2.27-.46c.67-.134 1.34-.27 2.01-.403.22-.043.443-.088.664-.106.31-.025.523.17.554.482.008.073.012.148.012.223.002 1.91.002 3.822 0 5.732z" />
  </svg>
);
const YouTubeMusicLogo = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm0 19.104c-3.924 0-7.104-3.18-7.104-7.104S8.076 4.896 12 4.896s7.104 3.18 7.104 7.104-3.18 7.104-7.104 7.104zm0-13.332c-3.432 0-6.228 2.796-6.228 6.228S8.568 18.228 12 18.228s6.228-2.796 6.228-6.228S15.432 5.772 12 5.772zM9.684 15.54V8.46L15.816 12l-6.132 3.54z" />
  </svg>
);
const GumroadLogo = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M15.826 15.069a1.018 1.018 0 1 1-.001 2.036 1.018 1.018 0 0 1 0-2.036zM21.327 1.11a1.018 1.018 0 1 1 .001 2.036 1.018 1.018 0 0 1 0-2.036zM3.322 3.107h16.116a2.13 2.13 0 0 0 1.89 1.151c1.174 0 2.129-.955 2.129-2.13A2.131 2.131 0 0 0 21.327 0c-.89 0-1.654.55-1.97 1.329H3.321C1.764 1.329.543 2.51.543 4.019v17.156C.543 22.706 1.816 24 3.322 24h17.155c1.51 0 2.738-1.267 2.738-2.825V10.998c0-1.532-1.228-2.78-2.738-2.78H10.3c-1.553 0-2.866 1.274-2.866 2.78v3.198c0 1.484 1.286 2.691 2.866 2.691h3.554a2.132 2.132 0 0 0 1.972 1.329c1.174 0 2.129-.956 2.129-2.13s-.955-2.129-2.13-2.129a2.13 2.13 0 0 0-1.889 1.152H10.3c-.523 0-1.088-.349-1.088-.913v-3.198c0-.524.519-1 1.088-1h10.177c.538 0 .96.439.96 1v10.177c0 .567-.44 1.047-.96 1.047H3.322c-.533 0-1-.49-1-1.047V4.02c0-.52.43-.912 1-.912" />
  </svg>
);

/* ============================================================
   SUBSCRIBER GATE LINK
   ============================================================ */
const SubscriberGateLink = ({ link, getButtonStyle }) => {
  const [gateEmail, setGateEmail] = useState('');
  const [gateLoading, setGateLoading] = useState(false);
  const [showGateInput, setShowGateInput] = useState(false);

  const handleSubscribeGate = async (e) => {
    e.preventDefault();
    if (!gateEmail || !gateEmail.includes('@')) return;
    setGateLoading(true);

    try {
      // ── STRICT VERIFICATION GATE ──
      // Instead of just adding them, we strictly verify if they are ALREADY a subscriber.
      const normalizedEmail = gateEmail.toLowerCase().trim();
      const subscriberAccess = readSubscriberAccess(link.profile_id);
      if (!subscriberAccess.token || subscriberAccess.email !== normalizedEmail) {
        toast.error('Access link required', { description: 'Use your confirmation email link, or subscribe again to receive a fresh access link.' });
        window.dispatchEvent(new Event('linkit_open_subscribe_modal'));
        return;
      }

      const res = await fetch('/api/v1/verify-sub', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: normalizedEmail,
          profile_id: link.profile_id,
          subscriber_token: subscriberAccess.token,
        })
      });

      if (res.ok) {
        storeSubscriberAccess(link.profile_id, normalizedEmail, subscriberAccess.token);
        toast.success('Identity Verified! 🎉', { description: 'Access Granted.' });
        setShowGateInput(false);
        window.dispatchEvent(new Event('linkit_gate_unlock'));
      } else {
        // Not a subscriber!
        toast.error('Access Denied 🛑', { description: 'You must be on the subscriber list to unlock this. Click the Bell icon to join!' });
        // Automatically open the main subscription modal for them!
        window.dispatchEvent(new Event('linkit_open_subscribe_modal'));
      }
    } catch {
      toast.error('Network error. Try again.');
    } finally {
      setGateLoading(false);
    }
  };

  return (
    <div className="w-full relative group cursor-pointer" onClick={() => !showGateInput && setShowGateInput(true)}>
      <div
        className="w-full py-4 px-5 flex items-center justify-between opacity-50 blur-[3px] pointer-events-none select-none transition-all group-hover:blur-[1px]"
        style={getButtonStyle()}
      >
        <div className="flex items-center gap-3">
          {link.thumbnail && <img src={link.thumbnail} loading="lazy" decoding="async" className="w-8 h-8 rounded-lg object-cover" alt="" />}
          <span className="font-bold" style={{ fontSize: 'inherit' }}>{link.title}</span>
        </div>
      </div>

      {!showGateInput ? (
        <div
          className="absolute inset-0 flex items-center justify-center gap-2 bg-black/5 backdrop-blur-[1px] rounded-inherit hover:bg-black/10 transition-all"
          style={{ borderRadius: 'inherit' }}
        >
          <span className="text-lg">🔒</span>
          <span className="font-black text-[10px] uppercase tracking-widest text-white drop-shadow-lg">Fan-Only Content</span>
        </div>
      ) : (
        <form
          onSubmit={handleSubscribeGate}
          onClick={e => e.stopPropagation()}
          className="absolute inset-0 flex items-center gap-2 px-3 bg-white/95 backdrop-blur-xl animate-in fade-in zoom-in-95 duration-200"
          style={{ borderRadius: 'inherit' }}
        >
          <input
            type="email"
            required
            value={gateEmail}
            onChange={e => setGateEmail(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 min-w-0 p-2 bg-transparent outline-none font-bold text-sm text-slate-900 placeholder-slate-400"
            autoFocus
          />
          <button
            type="submit"
            disabled={gateLoading}
            className="shrink-0 px-4 py-2 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all disabled:opacity-50"
          >
            {gateLoading ? '...' : 'Unlock'}
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); setShowGateInput(false); }} className="shrink-0 p-1 text-slate-400 hover:text-slate-900">
            <X size={14} />
          </button>
        </form>
      )}
    </div>
  );
};

/* ============================================================
   MOTION CANVAS
   ============================================================ */
const MotionCanvas = ({ type, color = '#FFFFFF', intensity = 5, speed = 1, density = 1, customData }) => {
  const count = Math.floor(intensity * 6 * density);
  const particles = useMemo(() => Array.from({ length: count }, (_, i) => {
    const seed = i + count * 13 + (type?.length || 0) * 37;
    return {
      id: i,
      x: seededUnit(seed + 1) * 100,
      y: seededUnit(seed + 2) * 100,
      size: 2 + seededUnit(seed + 3) * (type === 'confetti' ? 10 : 6),
      delay: seededUnit(seed + 4) * 8,
      duration: (4 + seededUnit(seed + 5) * 8) / speed,
      hue: seededUnit(seed + 6) * 360,
      driftX: seededUnit(seed + 7) * 50 - 25,
      shape: ['circle', 'square', 'triangle'][Math.floor(seededUnit(seed + 8) * 3)],
    };
  }), [count, type, speed]);
  const matrixColumns = useMemo(() => Array.from({ length: 20 }, (_, i) => ({
    delay: seededUnit(i + 101) * 5,
    duration: 2 + seededUnit(i + 151) * 3,
    bits: Array.from({ length: 30 }, (_, j) => seededUnit(i * 31 + j + 211) > 0.5 ? '1' : '0'),
  })), []);

  if (!type || type === 'none') return null;

  if (type === 'matrix') {
    return (
      <div className="absolute inset-0 pointer-events-none z-[1] flex justify-around opacity-20">
        {matrixColumns.map((column, i) => (
          <div key={i} className="flex flex-col gap-2 font-mono text-[10px] text-emerald-500 animate-matrix-drop" style={{ animationDelay: `${column.delay}s`, animationDuration: `${column.duration}s` }}>
            {column.bits.map((bit, j) => <span key={j}>{bit}</span>)}
          </div>
        ))}
      </div>
    );
  }

  if (type === 'clouds') {
    return (
      <div className="absolute inset-0 pointer-events-none z-[1]">
        {particles.slice(0, 10).map(p => (
          <motion.div
            key={p.id}
            initial={{ x: '-20%', y: `${p.y}%`, opacity: 0 }}
            animate={{ x: '120%', opacity: 0.4 }}
            transition={{ duration: p.duration * 4, repeat: Infinity, ease: 'linear', delay: p.delay }}
            className="absolute text-white/20 blur-xl"
            style={{ width: p.size * 20, height: p.size * 10 }}
          >
            <Cloud size={p.size * 20} />
          </motion.div>
        ))}
      </div>
    );
  }

  if (type === 'bokeh') {
    return (
      <div className="absolute inset-0 pointer-events-none z-[1]">
        {particles.map(p => (
          <motion.div
            key={p.id}
            animate={{ y: [0, -100, 0], x: [0, p.driftX, 0], scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: p.duration * 3, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute rounded-full blur-2xl"
            style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size * 8, height: p.size * 8, backgroundColor: color, filter: `hue-rotate(${p.hue}deg) blur(20px)` }}
          />
        ))}
      </div>
    );
  }

  if (type === 'lottie' && customData) {
    return (
      <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden">
        <iframe src={`https://embed.lottiefiles.com/animation/${customData}`} className="w-full h-full border-none pointer-events-none scale-150 opacity-40" />
      </div>
    );
  }

  if (type === 'gradient_mesh') {
    return (
      <div className="absolute inset-0 pointer-events-none z-[1]"
        style={{ background: `linear-gradient(45deg, ${color}22, transparent, ${color}44, transparent, ${color}22)`, backgroundSize: '400% 400%', animation: 'gradientShift 8s ease infinite', opacity: 0.7 }} />
    );
  }

  if (type === 'aurora') {
    return (
      <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden">
        {[0, 1, 2].map(i => (
          <div key={i} className="absolute inset-x-0 h-1/3"
            style={{ top: `${i * 30}%`, background: `linear-gradient(90deg, transparent, ${color}${['44', '66', '33'][i]}, transparent)`, filter: 'blur(40px)', animation: `auroraFlow ${6 + i * 2}s ease-in-out ${i * 1.5}s infinite` }} />
        ))}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden">
      {particles.map(p => (
        <div key={p.id} className="absolute"
          style={{
            left: `${p.x}%`,
            bottom: type === 'snow' ? 'auto' : undefined,
            top: type === 'snow' ? '-10px' : undefined,
            width: `${p.size}px`,
            height: `${p.size}px`,
            borderRadius: type === 'confetti' ? (p.shape === 'circle' ? '50%' : '2px') : '50%',
            background: type === 'confetti' ? `hsl(${p.hue}, 80%, 60%)` : color,
            opacity: type === 'sparkles' ? 0 : 0.7,
            animation: type === 'confetti' ? `confettiFall ${p.duration}s ${p.delay}s linear infinite` : type === 'sparkles' ? `twinkle ${p.duration * 0.6}s ${p.delay}s ease-in-out infinite` : `floatUp ${p.duration}s ${p.delay}s linear infinite`,
          }} />
      ))}
    </div>
  );
};

/* ============================================================
   SKIPER UI: HOVER IMAGE REVEAL
   ============================================================ */
const HoverImageReveal = ({ src, alt, children }) => {
  const [isHovered, setIsHovered] = useState(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Skiper UI signature spring physics
  const springConfig = { stiffness: 400, damping: 25 };
  const springX = useSpring(x, springConfig);
  const springY = useSpring(y, springConfig);

  // Motion values don't trigger React re-renders, so no throttle needed —
  // useSpring handles smooth interpolation at 60fps automatically
  const handleMouseMove = useCallback((e) => {
    x.set(e.clientX + 20);
    y.set(e.clientY + 20);
  }, [x, y]);

  // Early return when no src — avoids attaching mouse listeners entirely
  if (!src) return <>{children}</>;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseMove={handleMouseMove}
      className="relative w-full"
    >
      {children}
      {typeof window !== 'undefined' && (
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.5, rotate: 10 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              style={{
                x: springX,
                y: springY,
                position: 'fixed',
                top: 0,
                left: 0,
                pointerEvents: 'none',
                zIndex: 99999,
              }}
              className="w-48 h-48 sm:w-64 sm:h-64 rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.4)] border-4 border-white/20"
            >
              <img src={src} referrerPolicy="no-referrer" loading="lazy" decoding="async" className="w-full h-full object-cover" alt={alt || 'preview'} />
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

/* ============================================================
   CARD LAYER HELPER (Moved inside ProfileView)
   ============================================================ */

/* ============================================================
   COLLECTION MODAL
   ============================================================ */
const CollectionModal = ({ collection, onClose, profile }) => {
  const products = collection.collection_items || [];
  const [checkoutProduct, setCheckoutProduct] = useState(null);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: collection.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied! 🔗');
    }
  };

  const handleProductClick = (e, p) => {
    if (collection.advanced_features?.commerce?.enabled) {
      e.preventDefault();
      setCheckoutProduct(p);
    }
  };

  return (
    <>
      {checkoutProduct && <CheckoutModal product={collection.collection_items?.find(p => p.id === checkoutProduct.id) || checkoutProduct} profile={profile} onClose={() => setCheckoutProduct(null)} />}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[500] flex flex-col overflow-hidden"
          style={{
            backdropFilter: 'blur(60px)',
            WebkitBackdropFilter: 'blur(60px)',
            background: 'rgba(255,255,255,0.7)'
          }}
        >
          {/* Floating Action Bar */}
          <div className="absolute top-6 right-6 flex gap-3 z-[510]">
            <button
              onClick={handleShare}
              className="w-12 h-12 rounded-full bg-white/40 border border-white/20 shadow-xl backdrop-blur-md flex items-center justify-center text-slate-900 hover:scale-110 active:scale-95 transition-all"
            >
              <Share2 size={20} />
            </button>
            <button
              onClick={onClose}
              className="w-12 h-12 rounded-full bg-white/40 border border-white/20 shadow-xl backdrop-blur-md flex items-center justify-center text-slate-900 hover:scale-110 active:scale-95 transition-all"
            >
              <X size={20} />
            </button>
          </div>

          {/* Modal Content - Scrollable Area */}
          <div className="flex-1 overflow-y-auto no-scrollbar pt-20 pb-20 px-6">
            <div className="max-w-4xl mx-auto w-full space-y-12">

              {/* Immersive Header */}
              <div className="text-center space-y-3">
                <div className="flex justify-center gap-2 mb-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-lg">
                    <img src={profile?.avatar_url} loading="lazy" decoding="async" className="w-full h-full object-cover" alt="" />
                  </div>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">{collection.title}</h3>
                <div className="space-y-1">
                  <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">{products.length} Products</p>
                  {collection.affiliate_disclaimer !== false && (
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em]">Products may contain affiliate links</p>
                  )}
                </div>
              </div>

              {/* Premium Product Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 sm:gap-8">
                {products.map((p, i) => (
                  <motion.a
                    key={i}
                    href={sanitizeUrl(p.url) || '#'}
                    target={collection.advanced_features?.commerce?.enabled ? undefined : "_blank"}
                    rel="noopener noreferrer"
                    onClick={(e) => handleProductClick(e, p)}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex flex-col group"
                  >
                    <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-white shadow-xl border border-white/50 group-hover:shadow-2xl group-hover:-translate-y-2 transition-all duration-500">
                      {p.image ? (
                        <img src={p.image} referrerPolicy="no-referrer" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={p.name} />
                      ) : (
                        <div className="w-full h-full bg-slate-50 flex items-center justify-center text-5xl">{p.emoji || '🛍️'}</div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                      {/* Floating buy button on hover */}
                      <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                        <div className="w-full py-3 bg-white/90 backdrop-blur rounded-2xl text-center text-xs font-black uppercase tracking-widest shadow-xl">
                          {collection.advanced_features?.commerce?.enabled ? 'Buy Now' : 'View Product'}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 px-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-black text-sm text-slate-900 leading-tight flex-1">{p.name}</p>
                        <button className="opacity-40 hover:opacity-100"><Share2 size={14} /></button>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{p.store || 'Store'}</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" />
                        <span className="text-sm font-black text-slate-600">
                          {p.price ? formatItemPrice(p.price, p.currency_code || p.currency, getProfileCurrency(profile)) : 'Free'}
                        </span>
                        {p.original_price && (
                          <span className="text-xs text-slate-400 line-through">${p.original_price}</span>
                        )}
                      </div>
                    </div>
                  </motion.a>
                ))}
              </div>

              {products.length === 0 && (
                <div className="text-center py-20 bg-white/20 rounded-[3rem] border-2 border-dashed border-white/40">
                  <p className="text-slate-400 font-bold uppercase tracking-widest">Storefront coming soon</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

/* ============================================================
   SHOP VIEW — MOSAIC GRID
   ============================================================ */
const ShopView = ({ profile, onProductClick }) => {
  const products = profile?.shop_products || [];
  const layout = profile?.shop_layout || 'grid';
  const textColor = profile?.title_color || (profile?.bg_type === 'liquid' ? '#FFFFFF' : '#000000');

  const handleProductClick = (e, p) => {
    trackClick('shop_item', p.id, p.url);
    const forceNativeCheckout = p.requires_shipping === true || String(p.requires_shipping) === 'true' || p.is_ai === true || String(p.is_ai) === 'true';
    if ((!p.url || forceNativeCheckout) && onProductClick) {
      e.preventDefault();
      onProductClick(p);
    }
  };

  const trackClick = async (type, linkId = null, url = null) => {
    // SECURITY UPGRADE: Moving analytics to server-side tracking
    // This prevents "Fake Data Injection" from DevTools and adds IP-based deduplication.
    if (!profile?.id) return;

    fetch('/api/v1/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...buildAnalyticsPayload(),
        profile_id: profile.id,
        link_id: linkId,
        event_type: type || 'click',
        url: url || window.location.href,
        destination_platform: detectAnalyticsPlatform(url),
      })
    }).catch(() => {
      // Silently fail to not interrupt user experience
    });
  };

  if (products.length === 0 && !profile?.tip_jar?.enabled) return (
    <div className="text-center py-12 opacity-40">
      <p className="font-bold text-sm" style={{ color: textColor }}>No products yet</p>
    </div>
  );

  if (layout === 'list') return (
    <div className="space-y-8">
      {/* Global Tip Jar rendering */}
      {profile?.tip_jar?.enabled && (
        <div className="w-full rounded-[2.5rem] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-white/20" style={{ background: profile.button_color || '#ffffff', color: profile.link_text_color || '#000000' }}>
          <div className="p-8 space-y-5 text-center">
            <div className="text-5xl">{profile.tip_jar.emoji || '☕'}</div>
            <div>
              <h3 className="font-black text-xl tracking-tight leading-none">{profile.tip_jar.title || 'Support My Work'}</h3>
              {profile.tip_jar.message && <p className="text-sm opacity-70 mt-2 font-medium">{profile.tip_jar.message}</p>}
            </div>
            {profile.tip_jar.amounts?.length > 0 && (
              <div className="flex gap-2 justify-center flex-wrap">
                {profile.tip_jar.amounts.map((amt, i) => {
                  let finalUrl = profile.tip_jar.url || '#';
                  if (profile.tip_jar.platform === 'paypal' && !finalUrl.includes(amt)) {
                    finalUrl = `${finalUrl.replace(/\/$/, '')}/${amt}`;
                  } else if (profile.tip_jar.platform === 'upi' && !finalUrl.includes('upi://')) {
                    finalUrl = `upi://pay?pa=${finalUrl}&am=${amt}&cu=${profile.tip_jar.currency_code || 'INR'}`;
                  }

                  return (
                    <a key={i} href={finalUrl} target="_blank" rel="noopener noreferrer"
                      className="px-6 py-3 rounded-2xl font-black text-sm shadow-sm hover:scale-110 active:scale-95 transition-all"
                      style={{ background: profile.link_text_color || '#000000', color: profile.button_color || '#ffffff' }}>
                      {profile.tip_jar.currency_symbol || '$'}{amt}
                    </a>
                  );
                })}
              </div>
            )}
            <a href={sanitizeUrl(profile.tip_jar.url?.includes('upi://') || !profile.tip_jar.url ? profile.tip_jar.url : profile.tip_jar.platform === 'upi' ? `upi://pay?pa=${profile.tip_jar.url}` : profile.tip_jar.url)} target="_blank" rel="noopener noreferrer"
              className="block w-full py-4 rounded-3xl font-black text-sm uppercase tracking-widest shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all"
              style={{ background: profile.link_text_color || '#000000', color: profile.button_color || '#ffffff' }}>
              {profile.tip_jar.button_text || 'Support via UPI'}
            </a>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-30">Powered by Linkit</p>
          </div>
        </div>
      )}

      {products.length > 0 && profile.shop_title_position === 'header' && (
        <div className="text-center space-y-1 py-4">
          {profile.shop_title && (
            <h3 className="text-lg font-black tracking-tight uppercase" style={{ color: textColor }}>
              {profile.shop_title}
            </h3>
          )}
          <p className="text-[10px] font-black uppercase tracking-widest opacity-40" style={{ color: textColor }}>
            {products.length} {products.length === 1 ? 'product' : 'products'}
          </p>
          {profile.shop_description && (
            <p className="text-xs opacity-60 max-w-xs mx-auto font-medium" style={{ color: textColor }}>
              {profile.shop_description}
            </p>
          )}
        </div>
      )}

      {products.length === 0 ? (
        <div className="text-center py-12 opacity-40">
          <p className="font-bold text-sm" style={{ color: textColor }}>No products yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {products.map((p, i) => (
            <HoverImageReveal key={i} src={p.image} alt={p.name}>
              <a href={sanitizeUrl(p.url) || '#'} target={p.url ? "_blank" : "_self"} rel={p.url ? "noopener noreferrer" : ""}
                onClick={(e) => handleProductClick(e, p)}
                className="flex items-center gap-4 p-4 bg-black/10 rounded-2xl hover:bg-black/20 transition-all">
                {p.image ? (
                  <img src={p.image} referrerPolicy="no-referrer" loading="lazy" decoding="async" className="w-12 h-12 rounded-xl object-cover shrink-0" alt={p.name} />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-black/20 flex items-center justify-center text-2xl shrink-0">{p.emoji || '🛍️'}</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate" style={{ color: textColor }}>{p.name}</p>
                  {p.price && (
                    <p className="text-xs opacity-60" style={{ color: textColor }}>
                      {formatItemPrice(p.price, p.currency_code || p.currency, getProfileCurrency(profile))}
                    </p>
                  )}
                </div>
                <ChevronRight size={16} style={{ color: textColor }} className="opacity-40 shrink-0" />
              </a>
            </HoverImageReveal>
          ))}
        </div>
      )}

      {products.length > 0 && (!profile.shop_title_position || profile.shop_title_position === 'footer') && (
        <div className="text-center space-y-1 py-4">
          {profile.shop_title && (
            <h3 className="text-lg font-black tracking-tight uppercase" style={{ color: textColor }}>
              {profile.shop_title}
            </h3>
          )}
          <p className="text-[10px] font-black uppercase tracking-widest opacity-40" style={{ color: textColor }}>
            {products.length} {products.length === 1 ? 'product' : 'products'}
          </p>
          {profile.shop_description && (
            <p className="text-xs opacity-60 max-w-xs mx-auto font-medium" style={{ color: textColor }}>
              {profile.shop_description}
            </p>
          )}
        </div>
      )}
    </div>
  );

  // Mosaic / Grid (default — Linktree-style)
  return (
    <div className="space-y-8">
      {/* Global Tip Jar rendering */}
      {profile?.tip_jar?.enabled && (
        <div className="w-full rounded-[2.5rem] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-white/20" style={{ background: profile.button_color || '#ffffff', color: profile.link_text_color || '#000000' }}>
          <div className="p-8 space-y-5 text-center">
            <div className="text-5xl">{profile.tip_jar.emoji || '☕'}</div>
            <div>
              <h3 className="font-black text-xl tracking-tight leading-none">{profile.tip_jar.title || 'Support My Work'}</h3>
              {profile.tip_jar.message && <p className="text-sm opacity-70 mt-2 font-medium">{profile.tip_jar.message}</p>}
            </div>
            {profile.tip_jar.amounts?.length > 0 && (
              <div className="flex gap-2 justify-center flex-wrap">
                {profile.tip_jar.amounts.map((amt, i) => {
                  let finalUrl = profile.tip_jar.url || '#';
                  if (profile.tip_jar.platform === 'paypal' && !finalUrl.includes(amt)) {
                    finalUrl = `${finalUrl.replace(/\/$/, '')}/${amt}`;
                  } else if (profile.tip_jar.platform === 'upi' && !finalUrl.includes('upi://')) {
                    finalUrl = `upi://pay?pa=${finalUrl}&am=${amt}&cu=${profile.tip_jar.currency_code || 'INR'}`;
                  }

                  return (
                    <a key={i} href={finalUrl} target="_blank" rel="noopener noreferrer"
                      className="px-6 py-3 rounded-2xl font-black text-sm shadow-sm hover:scale-110 active:scale-95 transition-all"
                      style={{ background: profile.link_text_color || '#000000', color: profile.button_color || '#ffffff' }}>
                      {profile.tip_jar.currency_symbol || '$'}{amt}
                    </a>
                  );
                })}
              </div>
            )}
            <a href={sanitizeUrl(profile.tip_jar.url?.includes('upi://') || !profile.tip_jar.url ? profile.tip_jar.url : profile.tip_jar.platform === 'upi' ? `upi://pay?pa=${profile.tip_jar.url}` : profile.tip_jar.url)} target="_blank" rel="noopener noreferrer"
              className="block w-full py-4 rounded-3xl font-black text-sm uppercase tracking-widest shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all"
              style={{ background: profile.link_text_color || '#000000', color: profile.button_color || '#ffffff' }}>
              {profile.tip_jar.button_text || 'Support via UPI'}
            </a>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-30">Powered by Linkit</p>
          </div>
        </div>
      )}

      {products.length > 0 && (
        <div className="space-y-6">
          {(profile.shop_title_position === 'header') && (
            <div className="text-center space-y-1 py-4">
              {profile.shop_title && (
                <h3 className="text-lg font-black tracking-tight uppercase" style={{ color: textColor }}>
                  {profile.shop_title}
                </h3>
              )}
              <p className="text-[10px] font-black uppercase tracking-widest opacity-40" style={{ color: textColor }}>
                {products.length} {products.length === 1 ? 'product' : 'products'}
              </p>
              {profile.shop_description && (
                <p className="text-xs opacity-60 max-w-xs mx-auto font-medium" style={{ color: textColor }}>
                  {profile.shop_description}
                </p>
              )}
            </div>
          )}

          <div className={cn("grid gap-6 sm:gap-8", layout === 'shelf' ? 'grid-flow-col auto-cols-[180px] overflow-x-auto no-scrollbar pb-4' : products.length === 1 ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3')}>
            {products.map((product, idx) => (
              <motion.a
                key={product.id || idx}
                href={sanitizeUrl(product.url)}
                target={product.url ? "_blank" : "_self"}
                rel={product.url ? "noopener noreferrer" : ""}
                onClick={(e) => handleProductClick(e, product)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ x: -4, y: -4, boxShadow: '8px 8px 0 0 #000' }}
                whileTap={{ x: 2, y: 2, boxShadow: 'none' }}
                className={cn(
                  "group flex flex-col bg-white border-2 border-slate-900 will-change-transform overflow-hidden rounded-3xl",
                  "shadow-[4px_4px_0_0_#000]"
                )}
              >
                <div className="relative aspect-square">
                  {product.image ? (
                    <img src={product.image} referrerPolicy="no-referrer" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt={product.name} />
                  ) : (
                    <div className="w-full h-full bg-slate-50 flex items-center justify-center text-4xl">
                      {product.emoji || '🛍️'}
                    </div>
                  )}
                  {product.price && (
                    <div className="absolute bottom-2 right-2 px-3 py-1 bg-slate-900 text-white font-black text-[10px] rounded-lg shadow-xl">
                      {formatItemPrice(product.price, product.currency_code || product.currency, getProfileCurrency(profile))}
                    </div>
                  )}
                </div>

                <div className="p-4 flex flex-col gap-1 items-center text-center">
                  <h4 className="font-black text-sm uppercase tracking-tight line-clamp-1" style={{ color: textColor }}>{product.name}</h4>
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] opacity-40" style={{ color: textColor }}>
                    {profile?.shop_cta || 'Buy Now'}
                  </p>
                </div>
              </motion.a>
            ))}
          </div>

          {(!profile.shop_title_position || profile.shop_title_position === 'footer') && (
            <div className="text-center space-y-1 py-4">
              {profile.shop_title && (
                <h3 className="text-lg font-black tracking-tight uppercase" style={{ color: textColor }}>
                  {profile.shop_title}
                </h3>
              )}
              <p className="text-[10px] font-black uppercase tracking-widest opacity-40" style={{ color: textColor }}>
                {products.length} {products.length === 1 ? 'product' : 'products'}
              </p>
              {profile.shop_description && (
                <p className="text-xs opacity-60 max-w-xs mx-auto font-medium" style={{ color: textColor }}>
                  {profile.shop_description}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ============================================================
   LINK BLOCK
   ============================================================ */
const LiveVisitorDot = ({ profile, iconColor }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    // setCount initialized
    setCount(Math.floor(Math.random() * 15) + 2); // eslint-disable-line
    const interval = setInterval(() => {
      setCount(prev => {
        const change = Math.floor(Math.random() * 3) - 1;
        return Math.max(1, prev + change);
      });
    }, 5000 + Math.random() * 5000);
    return () => clearInterval(interval);
  }, []);

  if (!profile?.show_live_visitor_count) return null;

  return (
    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full backdrop-blur-sm" style={{ backgroundColor: hexToRgba(iconColor, 0.08), color: iconColor }}>
      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.8)]" />
      <span className="text-[10px] font-black uppercase tracking-widest">{count} Live</span>
    </div>
  );
};

const TestimonialRenderer = ({ link, getButtonStyle }) => {
  const btnStyle = getButtonStyle();
  return (
    <div style={btnStyle} className="p-6 relative text-left">
      <div className="text-2xl mb-2 opacity-50">"</div>
      <p className="text-sm italic font-medium leading-relaxed mb-4">{link.social_data?.quote || 'No review text provided.'}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs font-black uppercase tracking-widest">{link.social_data?.author || 'Anonymous'}</span>
        <div className="flex gap-1 text-[#FFD700]">
          {[...Array(5)].map((_, i) => (
            <span key={i} className={i < (link.social_data?.rating || 5) ? "" : "opacity-30"}>★</span>
          ))}
        </div>
      </div>
    </div>
  );
};

const MarqueeRenderer = ({ link, getButtonStyle }) => {
  const btnStyle = getButtonStyle();
  const speed = link.social_data?.speed === 'fast' ? 5 : link.social_data?.speed === 'slow' ? 20 : 10;
  return (
    <div style={{ ...btnStyle, overflow: 'hidden', padding: 0 }} className="relative h-[60px] flex items-center">
      <div className="whitespace-nowrap inline-block px-4 animate-marquee" style={{ animationDuration: `${speed}s`, animationTimingFunction: 'linear', animationIterationCount: 'infinite' }}>
        <span className="text-sm font-bold uppercase tracking-widest">{link.social_data?.text || 'Your announcement here'} • </span>
        <span className="text-sm font-bold uppercase tracking-widest">{link.social_data?.text || 'Your announcement here'} • </span>
        <span className="text-sm font-bold uppercase tracking-widest">{link.social_data?.text || 'Your announcement here'} • </span>
      </div>
    </div>
  );
};

const InstagramGridRenderer = ({ link, getButtonStyle }) => {
  const btnStyle = getButtonStyle();
  const username = link.social_data?.username || 'instagram';

  // Mock grid of 6 images
  const mockImages = [
    `https://picsum.photos/seed/${username}1/300/300`,
    `https://picsum.photos/seed/${username}2/300/300`,
    `https://picsum.photos/seed/${username}3/300/300`,
    `https://picsum.photos/seed/${username}4/300/300`,
    `https://picsum.photos/seed/${username}5/300/300`,
    `https://picsum.photos/seed/${username}6/300/300`,
  ];

  return (
    <div style={btnStyle} className="p-4 relative">
      <div className="flex items-center gap-2 mb-4">
        <InstagramIcon size={18} />
        <span className="text-xs font-bold uppercase tracking-widest">@{username}</span>
      </div>
      <div className="grid grid-cols-3 gap-1 rounded-lg overflow-hidden">
        {mockImages.map((src, i) => (
          <div key={i} className="aspect-square bg-black/10 relative group overflow-hidden">
            <img src={src} alt="IG Post" className="w-full h-full object-cover transition-transform group-hover:scale-110" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
              <InstagramIcon className="text-white w-5 h-5" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CountdownRenderer = ({ link, getButtonStyle }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    // Prefer native schema end_date over social_data for new exploding links
    const targetDate = new Date(link.end_date || link.social_data?.target_date || new Date().toISOString()).getTime();

    const updateTime = () => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      });
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [link.end_date, link.social_data?.target_date]);

  return (
    <div className="w-full rounded-[2rem] overflow-hidden shadow-xl text-center py-8 px-4" style={getButtonStyle()}>
      {link.title && <h3 className="font-black text-xl mb-6" style={{ fontSize: 'inherit' }}>{link.title}</h3>}
      {isExpired ? (
        <div className="text-2xl font-black uppercase tracking-widest animate-pulse">It's Here!</div>
      ) : (
        <div className="flex items-center justify-center gap-4 sm:gap-6">
          <div className="flex flex-col items-center">
            <span className="text-3xl sm:text-4xl font-black tabular-nums">{timeLeft.days}</span>
            <span className="text-[10px] uppercase font-bold tracking-widest opacity-60">Days</span>
          </div>
          <div className="text-2xl font-black opacity-30">:</div>
          <div className="flex flex-col items-center">
            <span className="text-3xl sm:text-4xl font-black tabular-nums">{String(timeLeft.hours).padStart(2, '0')}</span>
            <span className="text-[10px] uppercase font-bold tracking-widest opacity-60">Hours</span>
          </div>
          <div className="text-2xl font-black opacity-30">:</div>
          <div className="flex flex-col items-center">
            <span className="text-3xl sm:text-4xl font-black tabular-nums">{String(timeLeft.minutes).padStart(2, '0')}</span>
            <span className="text-[10px] uppercase font-bold tracking-widest opacity-60">Mins</span>
          </div>
          <div className="text-2xl font-black opacity-30">:</div>
          <div className="flex flex-col items-center">
            <span className="text-3xl sm:text-4xl font-black tabular-nums">{String(timeLeft.seconds).padStart(2, '0')}</span>
            <span className="text-[10px] uppercase font-bold tracking-widest opacity-60">Secs</span>
          </div>
        </div>
      )}
    </div>
  );
};

const LinkBlock = ({ link: originalLink, profile, iconColor, setTermsLink }) => {
  const [collectionOpen, setCollectionOpen] = useState(false);
  const [videoExpanded, setVideoExpanded] = useState(originalLink.auto_expand || false);
  const [dynamicFeedData, setDynamicFeedData] = useState(null);
  const prefersReducedMotion = useReducedMotion();

  // Dynamic Feed Resolution
  useEffect(() => {
    if (originalLink?.advanced_features?.dynamic_feed?.enabled && originalLink.advanced_features.dynamic_feed.feed_url) {
      fetch(originalLink.advanced_features.dynamic_feed.feed_url)
        .then(res => res.json())
        .then(data => setDynamicFeedData(data))
        .catch(err => console.warn('Dynamic feed fetch failed:', err));
    }
  }, [originalLink]);

  // A/B Testing & Feed Resolution
  const link = useMemo(() => {
    let resolved = { ...originalLink };

    // Apply Dynamic Feed first if available
    if (dynamicFeedData) {
      if (dynamicFeedData.title) resolved.title = dynamicFeedData.title;
      if (dynamicFeedData.image || dynamicFeedData.thumbnail) resolved.thumbnail = dynamicFeedData.image || dynamicFeedData.thumbnail;
      if (dynamicFeedData.url) resolved.url = dynamicFeedData.url;
    }

    if (resolved?.advanced_features?.ab_test?.enabled && resolved.advanced_features.ab_test.variant_b?.title) {
      const isVariantB = Math.random() > 0.5;
      if (isVariantB) {
        resolved.title = resolved.advanced_features.ab_test.variant_b.title || resolved.title;
        resolved.thumbnail = resolved.advanced_features.ab_test.variant_b.thumbnail || resolved.thumbnail;
      }
    }
    return resolved;
  }, [originalLink, dynamicFeedData]);

  const trackClick = async (destinationPlatform = '', destinationUrl = link.url || 'internal') => {
    // 1. Execute Retargeting Pixels if enabled
    if (link.advanced_features?.pixels?.enabled) {
      try {
        const { fb, tiktok } = link.advanced_features.pixels;
        // If Facebook Pixel is present on page and ID exists
        if (fb && typeof window !== 'undefined' && window.fbq) {
          window.fbq('trackCustom', 'LinkClick', { link_title: link.title, link_url: destinationUrl, pixel_id: fb });
        } else if (fb) {
          // Fallback image pixel injection
          const img = new Image();
          img.src = `https://www.facebook.com/tr?id=${fb}&ev=LinkClick&noscript=1`;
        }

        // If TikTok Pixel is present
        if (tiktok && typeof window !== 'undefined' && window.ttq) {
          window.ttq.track('ClickButton', { content_name: link.title, content_id: link.id });
        }
      } catch (e) {
        console.warn('Pixel tracking failed', e);
      }
    }

    // 2. Server-Side Analytics
    if (!profile?.id) return;

    fetch('/api/v1/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...buildAnalyticsPayload(),
        profile_id: profile.id,
        link_id: link.id,
        event_type: 'click',
        url: destinationUrl,
        destination_platform: destinationPlatform || detectAnalyticsPlatform(destinationUrl),
      })
    }).catch(() => { });
  };

  const btnRadius = profile?.button_radius ?? 16;
  const btnBg = link.bg_color || profile?.button_color || '#FFFFFF';
  const btnText = link.text_color || profile?.link_text_color || '#000000';
  const btnStyle = profile?.button_style || 'solid';
  const btnBorderWidth = profile?.button_border_width || 0;
  const linkFontFamily = link.font_family || link?.social_data?.font_family || profile?.link_font_family || profile?.font_family || 'inherit';
  useEffect(() => {
    if (linkFontFamily && linkFontFamily !== 'inherit') {
      loadGoogleFont(linkFontFamily);
    }
  }, [linkFontFamily]);

  const getButtonStyle = () => {
    const base = {
      background: btnBg,
      color: btnText,
      borderRadius: `${Math.min(btnRadius, 999)}px`,
      border: btnStyle === 'outline' ? `${Math.max(btnBorderWidth, 2)}px solid ${btnText}` : btnBorderWidth > 0 ? `${btnBorderWidth}px solid ${btnText}` : 'none',
      fontFamily: linkFontFamily,
      fontWeight: link.font_weight || profile?.link_font_weight || profile?.font_weight || 700,
      fontSize: link.font_size ? `${link.font_size}px` : profile?.link_font_size ? `${profile.link_font_size}px` : undefined,
      letterSpacing: profile?.font_spacing ? `${profile.font_spacing}px` : undefined,
      transform: 'translateZ(0)',
      willChange: 'transform, opacity',
      backfaceVisibility: 'hidden',
    };
    if (btnStyle === 'soft_shadow') return { ...base, boxShadow: `0 8px 24px rgba(0,0,0,0.12)` };
    if (btnStyle === 'hard_shadow') return { ...base, boxShadow: `8px 8px 0 0 ${btnText}`, border: `3px solid ${btnText}` };
    if (btnStyle === 'outline') return { ...base, background: 'transparent' };
    if (btnStyle === 'glass') return { ...base, background: btnBg.includes('rgba') ? btnBg : `${btnBg}1a`, backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: `1px solid ${btnText}22` };
    if (btnStyle === 'neon') return { ...base, background: 'transparent', color: btnBg, boxShadow: `0 0 20px ${btnBg}, inset 0 0 10px ${btnBg}`, border: `1px solid ${btnBg}`, textShadow: `0 0 8px ${btnBg}` };

    // New Styles
    if (btnStyle === 'skeuomorphic') return { ...base, background: `linear-gradient(180deg, ${btnBg} 0%, rgba(0,0,0,0.15) 100%)`, borderTop: `1px solid rgba(255,255,255,0.4)`, borderBottom: `1px solid rgba(0,0,0,0.4)`, boxShadow: `0 4px 6px rgba(0,0,0,0.1), inset 0 2px 4px rgba(255,255,255,0.3)` };
    if (btnStyle === 'clay') return { ...base, boxShadow: `inset 4px 4px 10px rgba(255,255,255,0.4), inset -4px -4px 10px rgba(0,0,0,0.1), 0 8px 20px rgba(0,0,0,0.1)` };
    if (btnStyle === 'brutalist') return { ...base, border: `4px solid ${btnText}`, borderRadius: '2px', boxShadow: `8px 8px 0 0 ${btnText}`, background: btnBg };
    if (btnStyle === 'wireframe') return { ...base, background: 'transparent', border: `1px dashed ${btnText}`, borderRadius: 0 };
    if (btnStyle === 'retro') return { ...base, border: `2px solid ${btnText}`, borderBottomWidth: '6px', borderRadius: '8px' };
    if (btnStyle === '3d') return { ...base, boxShadow: `0 6px 0 rgba(0,0,0,0.2)`, transform: 'translateY(-2px)' };
    if (btnStyle === 'floating') return { ...base, boxShadow: `0 20px 40px -10px rgba(0,0,0,0.2)`, transform: 'scale(1.02)' };
    if (btnStyle === 'minimal') return { ...base, background: 'transparent', border: 'none', boxShadow: 'none' };

    // Massive Library Styles
    if (btnStyle === 'neumorphic') return { ...base, background: btnBg, boxShadow: `6px 6px 12px rgba(0,0,0,0.1), -6px -6px 12px rgba(255,255,255,0.7)`, border: 'none' };
    if (btnStyle === 'cyberpunk') return { ...base, background: btnBg, color: btnText, border: `2px solid ${btnText}`, clipPath: 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)', borderRightWidth: '6px' };
    if (btnStyle === 'holographic') return { ...base, background: `linear-gradient(124deg, #ff2400, #e81d1d, #e8b71d, #e3e81d, #1de840, #1ddde8, #2b1de8, #dd00f3, #dd00f3)`, backgroundSize: '1800% 1800%', animation: 'rainbow 18s ease infinite', color: '#fff', border: 'none' };
    if (btnStyle === 'glass_dark') return { ...base, background: btnBg.includes('rgba') ? btnBg : `${btnBg}66`, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: `1px solid ${btnText}1a` };
    if (btnStyle === 'brutal_pop') return { ...base, background: btnBg, border: `4px solid ${btnText}`, boxShadow: `6px 6px 0 ${btnText}, inset -4px -4px 0 rgba(0,0,0,0.1)` };
    if (btnStyle === 'y2k') return { ...base, background: `linear-gradient(180deg, rgba(255,255,255,0.8) 0%, ${btnBg} 100%)`, border: `2px solid ${btnText}`, borderRadius: '999px', boxShadow: `inset 0 4px 6px rgba(255,255,255,0.8), 0 4px 10px rgba(0,0,0,0.2)` };
    if (btnStyle === 'gothic') return { ...base, background: btnBg === '#FFFFFF' ? '#111' : btnBg, color: btnText, border: `1px solid ${btnText}44`, letterSpacing: '2px', textTransform: 'uppercase' };
    if (btnStyle === 'pixel') return { ...base, background: btnBg, border: `4px solid ${btnText}`, fontFamily: '"Courier New", monospace', boxShadow: `inset -4px -4px 0px rgba(0,0,0,0.2)` };
    if (btnStyle === 'liquid') return { ...base, background: btnBg, borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%', animation: 'morph 8s ease-in-out infinite' };
    if (btnStyle === 'metallic') return { ...base, background: `linear-gradient(180deg, #f8f9fa 0%, #ced4da 50%, #adb5bd 51%, #e9ecef 100%)`, color: '#343a40', border: '1px solid #6c757d', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6), 0 2px 4px rgba(0,0,0,0.2)' };
    if (btnStyle === 'paper') return { ...base, border: `1px solid ${btnText}22`, boxShadow: '2px 2px 5px rgba(0,0,0,0.05), inset 0 0 20px rgba(0,0,0,0.02)', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.8\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100\' height=\'100\' filter=\'url(%23noise)\' opacity=\'0.08\'/%3E%3C/svg%3E")' };
    if (btnStyle === 'fabric') return { ...base, background: btnBg, backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.05) 2px, rgba(0,0,0,0.05) 4px), repeating-linear-gradient(-45deg, transparent, transparent 2px, rgba(0,0,0,0.05) 2px, rgba(0,0,0,0.05) 4px)', border: `1px dashed ${btnText}` };

    return base;
  };

  // Consistent spring transition config for all hover effects — prevents stiff/laggy default tween
  // Optimized premium spring: high stiffness for responsiveness, balanced damping for smoothness
  const hoverSpring = { type: 'spring', stiffness: 400, damping: 30, mass: 0.5 };

  const hoverAnim = profile?.hover_effect === 'pop'
    ? { scale: profile?.hover_pop_scale || 1.05, y: -4, filter: 'drop-shadow(0px 20px 25px rgba(0,0,0,0.15))', transition: hoverSpring }
    : profile?.hover_effect === 'fill'
      ? { scale: 1.015, backgroundColor: profile.hover_fill_color || btnText, color: profile.hover_fill_text_color || (btnStyle === 'outline' ? '#ffffff' : btnBg), borderColor: profile.hover_fill_color || btnText, transition: hoverSpring }
      : profile?.hover_effect === 'glow' ? { scale: 1.02, boxShadow: `0 0 ${profile?.hover_glow_size || 25}px ${profile?.hover_glow_color || btnBg}`, transition: hoverSpring }
        : profile?.hover_effect === 'lift' ? { y: -(profile?.hover_lift_height || 6), filter: 'drop-shadow(0px 20px 25px rgba(0,0,0,0.15))', transition: hoverSpring }
          : profile?.hover_effect === 'shrink' ? { scale: 0.96, transition: hoverSpring }
            : profile?.hover_effect === 'push_down' ? { scale: 0.98, y: 3, transition: hoverSpring }
              : { scale: 1.015, filter: 'drop-shadow(0px 10px 15px rgba(0,0,0,0.1))', transition: hoverSpring };

  // === Section Divider/Label ===
  if (link.type === 'section_label') {
    return (
      <div className="text-center pt-8 pb-3 px-6 rounded-2xl" style={link.bg_color ? { backgroundColor: link.bg_color } : undefined}>
        <h3
          className="text-sm font-black uppercase tracking-[0.25em]"
          style={{
            color: link.text_color || iconColor || profile?.title_color || '#000000',
            fontFamily: linkFontFamily,
            fontWeight: link.font_weight || undefined,
            fontSize: link.font_size ? `${link.font_size}px` : undefined,
            letterSpacing: profile.font_spacing ? `${profile.font_spacing * 1.5}px` : '0.25em'
          }}
        >
          {link.title}
        </h3>
        <div className="w-8 h-1 bg-current opacity-20 mx-auto mt-3 rounded-full" style={{ color: link.text_color || iconColor || profile?.title_color || '#000000' }} />
      </div>
    );
  }

  // === Testimonial ===
  if (link.type === 'testimonial') {
    return (
      <motion.div
        whileHover={hoverAnim}
        whileTap={{ scale: 0.98, transition: hoverSpring }}
        className="w-full relative"
      >
        <TestimonialRenderer link={link} getButtonStyle={getButtonStyle} />
      </motion.div>
    );
  }

  // === Marquee ===
  if (link.type === 'marquee') {
    return (
      <motion.div
        whileHover={hoverAnim}
        whileTap={{ scale: 0.98, transition: hoverSpring }}
        className="w-full relative"
      >
        <MarqueeRenderer link={link} getButtonStyle={getButtonStyle} />
      </motion.div>
    );
  }

  // === Instagram Grid ===
  if (link.type === 'instagram_grid') {
    return (
      <motion.div
        whileHover={hoverAnim}
        whileTap={{ scale: 0.98, transition: hoverSpring }}
        className="w-full relative"
      >
        <InstagramGridRenderer link={link} getButtonStyle={getButtonStyle} />
      </motion.div>
    );
  }

  // === Text / Heading Block ===
  if (link.type === 'heading') {
    return (
      <div className="w-full text-center py-6 px-4 rounded-3xl" style={link.bg_color ? { backgroundColor: link.bg_color } : undefined}>
        <h2
          className="text-2xl md:text-3xl font-black leading-tight"
          style={{
            color: link.text_color || iconColor || profile?.title_color || '#000000',
            fontFamily: linkFontFamily,
            fontWeight: link.font_weight || profile?.title_font_weight || profile?.font_weight || 900,
            fontSize: link.font_size ? `${link.font_size}px` : undefined
          }}
        >
          {link.title}
        </h2>
        {link.url && (
          <p className="mt-2 text-sm opacity-70" style={{ color: link.text_color || profile?.bio_color || iconColor || '#000000' }}>
            {link.url}
          </p>
        )}
      </div>
    );
  }


  // === Collection Group ===
  if (link.type === 'collection') {
    const items = link.collection_items || [];
    const layout = link.social_data?.layout || 'modal';
    const preview = items.slice(0, 4);

    if (layout === 'carousel') {
      return (
        <div className="w-full">
          <div className="w-full overflow-x-auto no-scrollbar snap-x flex gap-4 pb-4">
            {items.map((item, i) => (
              <a
                key={i}
                href={sanitizeUrl(item.url) || '#'}
                target={link.advanced_features?.commerce?.enabled ? undefined : "_blank"}
                rel="noopener noreferrer"
                className="snap-center shrink-0 w-[140px] md:w-[160px] flex flex-col gap-2 group"
                onClick={(e) => {
                  if (link.advanced_features?.commerce?.enabled) {
                    e.preventDefault();
                    setCheckoutProduct(item);
                  }
                }}
              >
                <div className="w-full aspect-[4/5] rounded-2xl overflow-hidden shadow-lg relative border border-white/10" style={{ background: btnBg }}>
                  {item.image ? <img src={item.image} referrerPolicy="no-referrer" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-all" alt="" />
                    : <div className="w-full h-full bg-black/10 flex items-center justify-center text-3xl">{item.emoji || '📦'}</div>}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3"><span className="text-white text-xs font-black uppercase tracking-widest px-2 py-1 bg-black/50 backdrop-blur rounded flex items-center gap-1">Buy <ExternalLink size={10} /></span></div>
                </div>
                <div>
                  <p className="font-bold text-sm truncate" style={{ color: iconColor }}>{item.title}</p>
                  {item.price && <p className="text-xs font-medium opacity-70" style={{ color: iconColor }}>{formatItemPrice(item.price, item.currency_code || item.currency, getProfileCurrency(profile))}</p>}
                </div>
              </a>
            ))}
          </div>
        </div>
      );
    }

    return (
      <>
        <motion.button
          whileHover={hoverAnim}
          whileTap={{ scale: 0.985 }}
          onClick={() => setCollectionOpen(true)}
          className="w-full flex items-center gap-3 py-3 px-5 text-left relative z-10"
          style={getButtonStyle()}
        >
          {/* Mosaic preview thumbnails */}
          {preview.length > 0 ? (
            <div className="shrink-0 w-12 h-12 rounded-xl overflow-hidden grid grid-cols-2 gap-0.5">
              {preview.slice(0, 4).map((item, i) => (
                item.image ? <img key={i} src={item.image} referrerPolicy="no-referrer" loading="lazy" decoding="async" className="w-full h-full object-cover" alt="" />
                  : <div key={i} className="bg-black/20 flex items-center justify-center text-sm">{item.emoji || '📦'}</div>
              ))}
            </div>
          ) : (
            <div className="w-12 h-12 rounded-xl bg-black/20 flex items-center justify-center text-2xl shrink-0">🗂️</div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm truncate">{link.title}</p>
            <p className="text-xs opacity-50">{items.length} items</p>
          </div>
          <ChevronRight size={16} className="opacity-40 shrink-0" />
        </motion.button>
        {collectionOpen && <CollectionModal collection={link} profile={profile} onClose={() => setCollectionOpen(false)} iconColor={iconColor} />}
      </>
    );
  }

  // === Video Embed ===
  if (link.type === 'video_embed') {
    const ytId = link.url?.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/)?.[1];
    const thumbnail = link.thumbnail || (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null);

    return (
      <div className="w-full rounded-3xl overflow-hidden" style={{ borderRadius: `${Math.min(btnRadius, 32)}px`, border: `1px solid rgba(255,255,255,0.1)` }}>
        {!videoExpanded ? (
          <button
            className="relative w-full block group"
            onClick={() => setVideoExpanded(true)}
          >
            {thumbnail ? (
              <img src={thumbnail} loading="lazy" decoding="async" className="w-full aspect-video object-cover" alt={link.title} />
            ) : (
              <div className="w-full aspect-video bg-black/30 flex items-center justify-center">
                <Play size={40} className="text-white/60" />
              </div>
            )}
            {/* Play button overlay */}
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-all flex items-center justify-center">
              <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                <Play size={22} className="text-slate-900 ml-0.5" fill="currentColor" />
              </div>
            </div>
            {/* Title bar */}
            {link.title && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-4">
                <p className="text-white font-bold text-sm text-left">{link.title}</p>
              </div>
            )}
          </button>
        ) : (
          <div className="w-full aspect-video bg-black">
            {ytId ? (
              <iframe
                src={`https://www.youtube.com/embed/${ytId}?autoplay=1`}
                className="w-full h-full"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video src={link.url} controls autoPlay preload="metadata" className="w-full h-full" />
            )}
          </div>
        )}
      </div>
    );
  }

  // === Spotify ===
  if (link.type === 'spotify') {
    const id = link.url?.split('/').pop()?.split('?')[0];
    const embedType = link.url?.includes('/track/') ? 'track' : link.url?.includes('/album/') ? 'album' : 'playlist';
    if (!id || !link.url) return null;
    return (
      <div className="w-full rounded-3xl overflow-hidden shadow-xl border border-white/10">
        <div className="flex items-center gap-3 px-4 py-3 bg-[#1ED760]/20 backdrop-blur-sm">
          <div className="w-8 h-8 bg-[#1ED760] rounded-xl flex items-center justify-center text-white"><SpotifyLogo /></div>
          <span className="font-bold text-xs opacity-80" style={{ color: iconColor || '#fff' }}>Spotify</span>
        </div>
        <iframe src={`https://open.spotify.com/embed/${embedType}/${id}?utm_source=generator`} width="100%" height="152" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" />
      </div>
    );
  }

  // === Apple Music ===
  if (link.type === 'apple_music') {
    const parts = link.url?.split('/');
    const albumId = parts?.pop()?.split('?')[0];
    const albumName = parts?.pop();
    if (!albumId || !albumName || !link.url) return null;
    return (
      <div className="w-full rounded-3xl overflow-hidden shadow-xl border border-white/10">
        <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-rose-500/20 to-pink-500/20 backdrop-blur-sm">
          <div className="w-8 h-8 bg-gradient-to-br from-rose-400 to-pink-600 rounded-xl flex items-center justify-center text-white"><AppleMusicLogo /></div>
          <span className="font-bold text-xs opacity-80" style={{ color: iconColor || '#fff' }}>Apple Music</span>
        </div>
        <iframe allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write" frameBorder="0" height="175" className="w-full" sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation" src={`https://embed.music.apple.com/us/album/${albumName}/${albumId}`} loading="lazy" />
      </div>
    );
  }

  // === YouTube Music ===
  if (link.type === 'yt_music') {
    const ytId = link.url?.split('v=')[1]?.split('&')[0] || link.url?.split('/').pop();
    if (!ytId || !link.url) return null;
    return (
      <div className="w-full rounded-3xl overflow-hidden shadow-xl border border-white/10">
        <div className="flex items-center gap-3 px-4 py-3 bg-[#FF0033]/20 backdrop-blur-sm">
          <div className="w-8 h-8 bg-[#FF0033] rounded-xl flex items-center justify-center text-white"><YouTubeMusicLogo /></div>
          <span className="font-bold text-xs opacity-80" style={{ color: iconColor || '#fff' }}>YouTube Music</span>
        </div>
        <div className="aspect-video bg-black">
          <iframe src={`https://www.youtube.com/embed/${ytId}`} width="100%" height="100%" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy" />
        </div>
      </div>
    );
  }

  // === Calendly Embed ===
  if (link.type === 'calendly') {
    const calendlyUrl = sanitizeCalendlyUrl(link.url);
    return (
      <div className="w-full rounded-[2rem] overflow-hidden shadow-xl" style={getButtonStyle()}>
        <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10" style={{ backgroundColor: 'rgba(0,0,0,0.05)' }}>
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white text-xl font-black shadow-inner" style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}>📅</div>
          <div className="flex-1 min-w-0 text-left">
            <h3 className="font-black text-sm tracking-tight truncate">{link.title || 'Schedule Time'}</h3>
            <p className="text-[10px] opacity-60 uppercase tracking-widest mt-0.5">Booking Module</p>
          </div>
        </div>
        <div className="bg-white">
          {calendlyUrl && (
            <iframe src={calendlyUrl} width="100%" height="500" frameBorder="0" scrolling="no" className="w-full" />
          )}
        </div>
      </div>
    );
  }

  // === Newsletter / Mailing List ===
  if (link.type === 'newsletter' || link.type === 'mailing_list') {
    const data = link.social_data || {};
    return <NewsletterBlock link={link} profile={profile} data={data} getButtonStyle={getButtonStyle} />;
  }

  // === Tip Jar ===
  if (link.type === 'tip_jar') {
    const data = link.social_data || {};
    const platformId = data.platform || 'custom';
    const currency = data.currency_symbol || '$';

    // Invert btnBg and btnText for explicit custom support rendering, guaranteeing contrast.
    const PLATFORM_STYLES = {
      paypal: { name: 'PayPal', color: '#003087', text: '#fff' },
      kofi: { name: 'Ko-fi', color: '#29ABE0', text: '#fff' },
      stripe: { name: 'Stripe', color: '#635BFF', text: '#fff' },
      upi: { name: 'UPI', color: '#0CAF60', text: '#fff' },
      custom: { name: 'Support', color: btnText, text: btnBg }
    };
    const platform = PLATFORM_STYLES[platformId] || PLATFORM_STYLES.custom;

    const amounts = Array.isArray(data.amounts) ? data.amounts : (data.amounts || '3,5,10').split(',').map(s => s.trim()).filter(Boolean);

    // Smart URL Builder for the main button
    const getFinalUrl = (amt = null) => {
      let base = link.url || '';
      if (!base) return '#';

      if (platformId === 'upi' && !base.includes('upi://')) {
        base = `upi://pay?pa=${base}${amt ? `&am=${amt}&cu=${data.currency_code || 'INR'}` : ''}`;
      } else if (platformId === 'paypal' && amt) {
        base = `${base.replace(/\/$/, '')}/${amt}`;
      }
      return base;
    };

    return (
      <div className="w-full rounded-[2rem] overflow-hidden shadow-xl" style={getButtonStyle()}>
        <div className="p-6 space-y-4 text-center">
          <div className="text-4xl">{data.emoji || '☕'}</div>
          <div>
            <h3 className="font-black text-lg tracking-tight">{link.title || 'Support Me'}</h3>
            {data.thank_you && <p className="text-sm opacity-70 mt-1">{data.thank_you}</p>}
          </div>
          {amounts.length > 0 && (
            <div className="flex gap-2 justify-center flex-wrap">
              {amounts.map((amt, i) => (
                <motion.a key={i} href={getFinalUrl(amt)} target="_blank" rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-5 py-2.5 rounded-xl font-black text-sm shadow-sm will-change-transform"
                  style={{ background: platform.color, color: platform.text }}>
                  {currency}{amt}
                </motion.a>
              ))}
            </div>
          )}
          <motion.a href={getFinalUrl()} target="_blank" rel="noopener noreferrer"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="block w-full py-3.5 rounded-2xl font-black text-sm uppercase tracking-widest shadow-lg will-change-transform"
            style={{ background: platform.color, color: platform.text }}>
            {data.button_text || `Support via ${platform.name}`}
          </motion.a>
          <p className="text-[9px] font-black uppercase tracking-widest" style={{ opacity: 0.4 }}>Powered by {platform.name}</p>
        </div>
      </div>
    );
  }

  // === Gumroad Product Embed ===
  if (link.type === 'gumroad') {
    const slug = link.url?.split('/l/')?.[1]?.split('?')?.[0] || '';
    const embedUrl = `https://gumroad.com/l/${slug}`;
    return (
      <div className="w-full rounded-[2rem] overflow-hidden shadow-xl" style={getButtonStyle()}>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-black" style={{ background: '#FF90E8' }}><GumroadLogo /></div>
            <div>
              <h3 className="font-black" style={{ fontSize: 'inherit' }}>{link.title}</h3>
              <p className="text-[10px] opacity-60">Digital Product · Gumroad</p>
            </div>
          </div>
          <a href={embedUrl} target="_blank" rel="noopener noreferrer"
            className="block w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest text-center shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-transform"
            style={{ background: 'linear-gradient(135deg,#FF90E8,#FF3BD4)', color: '#fff' }}>
            Get It Now →
          </a>
          <p className="text-center text-[9px] font-black uppercase tracking-widest opacity-30">Secure Checkout via Gumroad</p>
        </div>
      </div>
    );
  }

  // === Native Image Block ===
  if (link.type === 'image_block' && (link.thumbnail || link.url)) {
    return (
      <div className="w-full rounded-[2rem] overflow-hidden shadow-xl transition-transform hover:scale-[1.01]" style={getButtonStyle()}>
        <img src={link.thumbnail || link.url} alt={link.title} loading="lazy" decoding="async" className="w-full h-auto object-cover" />
        {link.title && link.title !== 'Image' && (
          <div className="p-5 text-center bg-white/5 backdrop-blur-sm border-t border-white/10">
            <h3 className="font-black tracking-tight" style={{ fontSize: 'inherit' }}>{link.title}</h3>
          </div>
        )}
      </div>
    );
  }

  // === Text Block ===
  if (link.type === 'text_block') {
    return (
      <div className="w-full rounded-[2rem] p-6 shadow-xl text-center space-y-4" style={getButtonStyle()}>
        {link.title && <h3 className="font-black text-xl tracking-tight">{link.title}</h3>}
        {link.social_data?.text && (
          <p className="text-sm font-medium leading-relaxed opacity-90 whitespace-pre-wrap">{link.social_data.text}</p>
        )}
        {link.social_data?.button_label && link.social_data?.button_url && (
          <a href={link.social_data.button_url} target="_blank" rel="noopener noreferrer"
            className="inline-block mt-2 px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest bg-black/10 hover:bg-black/20 transition-all">
            {link.social_data.button_label}
          </a>
        )}
      </div>
    );
  }

  // === Image Gallery ===
  if (link.type === 'image_gallery') {
    const items = link.collection_items || [];
    if (items.length > 0) {
      return (
        <div className="w-full rounded-[2rem] overflow-hidden shadow-xl p-4 sm:p-6" style={getButtonStyle()}>
          {link.title && <div className="text-center font-black text-lg mb-4">{link.title}</div>}
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <style>{`.hide-scroll::-webkit-scrollbar { display: none; }`}</style>
            {items.map((item, idx) => item.image && (
              <div key={idx} className="shrink-0 w-64 sm:w-72 snap-center relative rounded-2xl overflow-hidden group shadow-md hide-scroll">
                <img src={item.image} alt={item.title} loading="lazy" className="w-full h-48 sm:h-56 object-cover transition-transform group-hover:scale-105" />
                {item.title && (
                  <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent text-white">
                    <p className="text-xs sm:text-sm font-bold">{item.title}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }
  }

  // === Featured Link ===
  if (link.type === 'featured_link') {
    return (
      <a href={link.url} target="_blank" rel="noopener noreferrer" className="block w-full rounded-[2rem] overflow-hidden shadow-2xl transition-all hover:scale-[1.02]" style={getButtonStyle()}>
        {link.thumbnail && (
          <div className="w-full aspect-[21/9] relative">
            <img src={link.thumbnail} alt={link.title} className="fixed inset-0 w-full h-full object-cover pointer-events-none -z-10" />
            <div className="absolute inset-0 bg-black/10 transition-colors" />
          </div>
        )}
        <div className="p-6 text-center bg-white/5 backdrop-blur-sm border-t border-white/10">
          <h3 className="font-black text-xl tracking-tight">{link.title || 'Featured'}</h3>
          {link.social_data?.text && <p className="text-sm font-medium opacity-80 mt-2">{link.social_data.text}</p>}
        </div>
      </a>
    );
  }

  // === FAQ Accordion Block ===
  if (link.type === 'faq') {
    const questions = link.collection_items || [];
    return (
      <div className="w-full rounded-[2rem] overflow-hidden shadow-xl" style={getButtonStyle()}>
        {link.title && <div className="p-5 font-black text-center border-b border-white/10 text-lg">{link.title}</div>}
        <div className="divide-y divide-white/10">
          {questions.map((q, idx) => (
            <details key={idx} className="group p-5 hover:bg-white/5 transition-colors cursor-pointer">
              <summary className="font-bold list-none flex justify-between items-center outline-none">
                {q.title}
                <ChevronRight size={16} className="opacity-50 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="mt-4 text-sm opacity-80 leading-relaxed font-medium whitespace-pre-wrap">
                {q.description}
              </p>
            </details>
          ))}
        </div>
      </div>
    );
  }

  // === Map Block ===
  if (link.type === 'map') {
    const lat = link.social_data?.lat;
    const lng = link.social_data?.lng;
    const zoom = link.social_data?.zoom || 14;
    const hasCoordinates = lat != null && lng != null;

    return (
      <div className="w-full rounded-[2rem] overflow-hidden shadow-xl relative z-0" style={getButtonStyle()}>
        {link.title && <div className="p-4 border-b border-white/10 font-black text-center relative z-10 bg-inherit">{link.title}</div>}
        {hasCoordinates ? (
          <div style={{ height: '300px', width: '100%', filter: profile.theme === 'dark' || profile.theme === 'neo-brutalism' ? 'invert(1) hue-rotate(180deg) brightness(95%) contrast(90%)' : 'none' }}>
            <MapContainer center={[lat, lng]} zoom={zoom} scrollWheelZoom={false} style={{ height: '100%', width: '100%', zIndex: 0 }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[lat, lng]} />
            </MapContainer>
          </div>
        ) : (
          <div className="w-full h-[300px] flex items-center justify-center flex-col gap-2 p-6 text-center opacity-70">
            <MapPin size={32} />
            <p className="text-sm font-bold">Please configure the map location in your editor.</p>
          </div>
        )}
      </div>
    );
  }

  // === Countdown Block ===
  if (link.type === 'countdown') {
    return <CountdownRenderer link={link} getButtonStyle={getButtonStyle} />;
  }

  // === vCard Generation ===
  if (link.type === 'contact_card') {
    const vcardClick = () => {
      const cd = link.social_data || {};
      const vCardText = `BEGIN:VCARD
VERSION:3.0
FN:${cd.firstName || ''} ${cd.lastName || ''}
N:${cd.lastName || ''};${cd.firstName || ''};;;
ORG:${cd.company || ''}
TITLE:${cd.jobTitle || ''}
TEL;TYPE=CELL:${cd.phone || ''}
EMAIL;TYPE=PREF,INTERNET:${cd.email || ''}
END:VCARD`;
      const blob = new Blob([vCardText], { type: 'text/vcard' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${cd.firstName || 'contact'}.vcf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    return (
      <button onClick={vcardClick} className="w-full py-4 px-5 flex items-center justify-between transition-all group overflow-hidden" style={getButtonStyle()}>
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-black/10">👤</div>
          <div className="text-left">
            <p className="font-bold tracking-tight" style={{ fontSize: 'inherit' }}>{link.title}</p>
            <p className="text-xs opacity-60 font-medium">Click to save to contacts</p>
          </div>
        </div>
        <ChevronRight size={16} className="opacity-40 group-hover:translate-x-1 transition-transform" />
      </button>
    );
  }

  // === Tour Dates / Events ===
  if (link.type === 'tour_dates') {
    const events = link.social_data?.events || [];
    const formatDate = (iso) => {
      if (!iso) return { month: 'TBD', day: '' };
      const d = new Date(iso + 'T00:00:00'); // force local parse
      return {
        month: d.toLocaleString('en-US', { month: 'short' }).toUpperCase(),
        day: d.getDate(),
      };
    };
    return (
      <div className="w-full rounded-[2rem] overflow-hidden shadow-xl transition-all" style={getButtonStyle()}>
        <div className="p-5 flex items-center gap-3 border-b" style={{ borderColor: `${btnText}22` }}>
          <span className="text-lg">🎤</span>
          <h3 className="font-black text-sm uppercase tracking-widest">{link.title || 'Tour Dates'}</h3>
        </div>
        <div className="flex flex-col divide-y" style={{ '--divider': `${btnText}22` }}>
          {events.length === 0 ? (
            <p className="p-8 text-center text-sm font-bold opacity-40">No upcoming dates announced.</p>
          ) : (
            events.map((ev, idx) => {
              const { month, day } = formatDate(ev.date);
              return (
                <div key={idx} className="flex items-center justify-between px-5 py-4 hover:brightness-[1.08] transition-all" style={{ borderColor: `${btnText}22`, borderBottomWidth: idx < events.length - 1 ? 1 : 0 }}>
                  <div className="flex gap-4 items-center min-w-0">
                    <div className="flex flex-col text-center w-10 shrink-0">
                      <span className="text-[9px] font-black uppercase opacity-50 tracking-wider leading-none">{month}</span>
                      <span className="text-2xl font-black leading-none mt-0.5">{day}</span>
                    </div>
                    <span className="font-bold text-sm truncate">{ev.venue || 'Venue TBA'}</span>
                  </div>
                  {ev.is_sold_out ? (
                    <span className="shrink-0 ml-4 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest opacity-40 border" style={{ borderColor: btnText, color: btnText }}>
                      Sold Out
                    </span>
                  ) : ev.ticket_url ? (
                    <a href={sanitizeUrl(ev.ticket_url)} target="_blank" rel="noopener noreferrer"
                      className="shrink-0 ml-4 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm hover:scale-105 active:scale-95 transition-transform"
                      style={{ background: btnText, color: btnBg }}>
                      Tickets
                    </a>
                  ) : (
                    <span className="shrink-0 ml-4 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest opacity-30 border" style={{ borderColor: btnText, color: btnText }}>
                      Soon
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  // === Standard Audio Embedding (Fallback for music_embed) ===
  if (link.type === 'music_embed') {
    if (link.url?.includes('spotify.com')) {
      const id = link.url.split('/').pop()?.split('?')[0];
      const type = link.url.includes('/track/') ? 'track' : 'album';
      return (
        <div className="w-full rounded-3xl overflow-hidden shadow-xl" style={getButtonStyle()}>
          <iframe src={`https://open.spotify.com/embed/${type}/${id}?utm_source=generator`} width="100%" height="152" frameBorder="0" />
        </div>
      );
    }
  }



  // ─────────────────────────────────────────────────────────────
  // CRITICAL SECURITY: GATE VERIFICATION
  // This must happen before ANY other link-type rendering
  // ─────────────────────────────────────────────────────────────
  if (link.is_gated) {
    const isSubscribed = Boolean(readSubscriberAccess(link.profile_id).token);

    if (link.password && !isSubscribed) {
      return (
        <button
          onClick={handleUnlock}
          className="w-full py-4 px-5 flex items-center justify-between transition-all hover:scale-[1.01]"
          style={getButtonStyle()}
        >
          <div className="flex items-center gap-3">
            {link.thumbnail && <img src={link.thumbnail} loading="lazy" decoding="async" className="w-8 h-8 rounded-lg object-cover" alt="" />}
            <span className="font-bold" style={{ fontSize: 'inherit' }}>{link.title}</span>
          </div>
          <span className="text-lg">🔒</span>
        </button>
      );
    }

    if (!link.password && !isSubscribed) {
      return <SubscriberGateLink link={link} getButtonStyle={getButtonStyle} />;
    }
  }
  // ─────────────────────────────────────────────────────────────

  // === Social Icons Block ===
  if (link.type === 'social_icons') {
    const socialData = link.social_data || {};
    const active = PLATFORMS.filter(p => socialData[p.id]);
    if (active.length === 0) return null;
    const blockSocialColor = link.text_color || profile.social_icon_color || getProfileAccentColor(profile, iconColor);
    const firstIcon = getSocialIconProps({ profile, platform: active[0], iconColor: blockSocialColor, size: 44 });

    return (
      <div className={cn('w-full flex flex-wrap justify-center py-2', firstIcon.wrapperClassName)}>
        {active.map(p => {
          const iconProps = getSocialIconProps({ profile, platform: p, iconColor: blockSocialColor, size: 44 });
          return (
            <a key={p.id} href={sanitizeUrl(socialData[p.id])} target="_blank" rel="noopener noreferrer"
              onClick={() => trackClick(p.label, socialData[p.id])}
              className={iconProps.className}
              style={iconProps.style}>
              {getSocialIconNode(p, profile)}
            </a>
          );
        })}
      </div>
    );
  }

  // === Standard Link Button ===
  const detected = detectPlatformFromUrl(link.url);
  const routedHref = getPublicLinkHref(link);
  const isRoutedLink = canUseAttributionRoute(link);

  const handleUnlock = async () => {
    let input = '';
    const gateType = link.gate_type || (link.password ? 'password' : 'password');

    if (gateType === 'password') {
      input = await promptAction({
        title: 'Unlock this link',
        description: `Enter the password provided for “${link.title}”.`,
        label: 'Password',
        type: 'password',
        submitLabel: 'Unlock link',
      });
      if (!input) return;
    } else if (gateType === 'email') {
      input = await promptAction({
        title: 'Unlock this link',
        description: `Enter your email to unlock “${link.title}”.`,
        label: 'Email Address',
        type: 'email',
        submitLabel: 'Unlock link',
      });
      if (!input) return;
    } else if (gateType === 'follow') {
      const confirmed = await confirmAction({
        title: 'Unlock this link',
        description: `Follow ${link.gate_target || 'this creator'} to unlock “${link.title}”.`,
        confirmLabel: 'Follow & Unlock',
        cancelLabel: 'Cancel',
        tone: 'default'
      });
      if (!confirmed) return;
      if (link.gate_target) {
        window.open(`https://x.com/${link.gate_target.replace('@', '')}`, '_blank');
      }
      input = 'followed';
    }

    if (!profile?.id) {
      toast.error('Preview mode: Password verification disabled.');
      return;
    }

    const toastId = toast.loading('Verifying access...');
    const subscriberAccess = readSubscriberAccess(profile.id);

    try {
      const res = await fetch('/api/v1/unlock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          link_id: link.id,
          gate_input: input,
          gate_type: gateType,
          email: subscriberAccess.email,
          subscriber_token: subscriberAccess.token,
        })
      });

      const result = await res.json();

      if (!res.ok) {
        toast.error(result.error || 'Access Denied.', { id: toastId });
      } else {
        toast.success('Unlocked!', { id: toastId });
        window.open(sanitizeUrl(result.url), '_blank', 'noopener,noreferrer');
        trackClick();
      }
    } catch {
      toast.error('The link could not be unlocked. Try again later.', { id: toastId });
    }
  };



  const btnContent = (
    <>
      {(link.thumbnail || detected) && (
        <div className="shrink-0 relative z-10">
          {link.thumbnail ? (
            <img src={link.thumbnail} loading="lazy" decoding="async" className="w-10 h-10 rounded-xl object-cover shadow-sm group-hover:scale-110 transition-transform" alt="" />
          ) : detected ? (
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md group-hover:rotate-6 transition-transform"
              style={{ background: detected.gradient || detected.color }}
            >
              <div className="scale-90">{detected.svg}</div>
            </div>
          ) : null}
        </div>
      )}
      <span className="font-bold flex-1 relative z-10 flex items-center gap-2" style={{ fontSize: 'inherit' }}>
        {link.title}
        {link.is_sensitive && <AlertCircle size={14} className="text-rose-500" />}
      </span>
      <ExternalLink size={14} className="opacity-0 group-hover:opacity-40 transition-opacity shrink-0 relative z-10" />
    </>
  );

  const anchorProps = {
    href: link.is_gated ? "#" : routedHref,
    target: link.is_gated ? "_self" : "_blank",
    rel: "noopener noreferrer",
    onClick: async (e) => {
      if (link.is_sensitive) {
        e.preventDefault();
        const confirmed = await confirmAction({
          title: 'Sensitive Content',
          description: 'This link may contain sensitive content. Are you sure you want to proceed?',
          confirmLabel: 'Proceed',
          cancelLabel: 'Go Back',
          tone: 'danger'
        });

        if (!confirmed) return;

        if (link.is_gated) {
          handleUnlock();
        } else if (link.requires_terms) {
          setTermsLink(link);
        } else {
          if (!isRoutedLink) trackClick();
          window.open(routedHref, "_blank", "noopener,noreferrer");
        }
        return;
      }

      if (link.is_gated) {
        e.preventDefault();
        handleUnlock();
      } else if (link.requires_terms) {
        e.preventDefault();
        setTermsLink(link);
      } else {
        if (!isRoutedLink) trackClick();
      }
    },
    className: "w-full py-4 px-5 flex items-center gap-4 group overflow-hidden relative block z-10 will-change-transform",
    style: getButtonStyle()
  };

  const hasAnimation = link.animation_type && link.animation_type !== 'none';
  const spotlightProps = hasAnimation ? {
    animate: link.animation_type === 'pulse' ? { scale: [1, 1.02, 1] } :
      link.animation_type === 'bounce' ? { y: [0, -4, 0] } :
        link.animation_type === 'wobble' ? { rotate: [0, -1, 1, -1, 1, 0] } : {},
    transition: { repeat: Infinity, duration: 2, ease: "easeInOut" }
  } : {};

  if (link.is_priority) {
    const overdriveStyle = {
      background: '#F8E71C',
      color: '#050505',
      border: '4px solid #050505',
      borderRadius: '6px',
      fontFamily: linkFontFamily,
      fontWeight: 900,
      fontSize: profile?.link_font_size ? `${Math.max(profile.link_font_size, 16)}px` : undefined,
      letterSpacing: '0',
      transform: 'translateZ(0)',
      willChange: 'transform',
      backfaceVisibility: 'hidden',
    };

    return (
      <HoverImageReveal src={link.thumbnail} alt={link.title}>
        <div className="relative w-full pb-2 pr-2">
          <motion.div
            aria-hidden="true"
            className="absolute inset-0 translate-x-2 translate-y-2 rounded-[6px] bg-black"
            animate={prefersReducedMotion ? undefined : { x: [8, 11, 8], y: [8, 11, 8], scale: [1, 1.015, 1] }}
            transition={prefersReducedMotion ? undefined : { duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.a
            {...anchorProps}
            style={overdriveStyle}
            whileHover={prefersReducedMotion ? undefined : { x: -2, y: -2 }}
            whileTap={{ x: 6, y: 6 }}
            className={cn(anchorProps.className, "min-h-[76px] uppercase tracking-normal")}
          >
            <span className="relative z-10 rounded-full border-2 border-black bg-black px-2 py-1 text-[9px] font-black uppercase tracking-widest text-yellow-300">
              Overdrive
            </span>
            <span className="relative z-10 flex-1 break-words font-black leading-tight">
              [ {link.title} ]
            </span>
            <span className="relative z-10 h-5 w-2 bg-black" aria-hidden="true" />
          </motion.a>
        </div>
      </HoverImageReveal>
    );
  }

  if (profile?.hover_effect === 'magnetic') {
    return (
      <HoverImageReveal src={link.thumbnail} alt={link.title}>
        <MagneticTether strength={0.4}>
          <motion.a
            {...anchorProps}
            {...spotlightProps}
            whileHover={{ scale: 1.01, x: -2, y: -2, transition: hoverSpring }}
            whileTap={{ scale: 0.98, x: 1, y: 1 }}
            className={cn(anchorProps.className, "will-change-transform")}
          >
            {btnContent}
          </motion.a>
        </MagneticTether>
      </HoverImageReveal>
    );
  }

  return (
    <HoverImageReveal src={link.thumbnail} alt={link.title}>
      <motion.a {...anchorProps} {...spotlightProps} whileHover={hoverAnim} whileTap={{ scale: 0.985 }}>
        {btnContent}
      </motion.a>
    </HoverImageReveal>
  );
};

/* ============================================================
   NEWSLETTER BLOCK — Turnstile protected + Double Opt-In
   ============================================================ */
const NewsletterBlock = ({ link, profile, data, getButtonStyle }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [doneMessage, setDoneMessage] = useState('Check your inbox to confirm!');
  const [turnstileToken, setTurnstileToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      toast.error('Bot check not complete. Please wait a moment.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/v1/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          profile_id: profile?.id,
          profile_username: profile?.username,
          turnstile_token: turnstileToken,
        }),
      });
      const result = await res.json();
      if (!res.ok) {
        if (res.status === 409) {
          setDoneMessage(result.error || 'You are already on this list.');
          setDone(true);
          toast.error(result.error || 'You are already on this list.');
        } else {
          toast.error(result.error || 'Something went wrong.');
        }
      } else {
        setDoneMessage('Check your inbox to confirm!');
        setDone(true);
        toast.success('Check your email to confirm your subscription!');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full rounded-3xl overflow-hidden transition-all shadow-xl bg-white/10 backdrop-blur" style={getButtonStyle()}>
      <div className="p-6 space-y-4">
        {link.title && <h3 className="font-black text-center tracking-tight" style={{ fontSize: 'inherit' }}>{link.title}</h3>}
        {data.headline && <p className="text-center text-sm font-medium opacity-80">{data.headline}</p>}
        {done ? (
          <div className="text-center py-4 space-y-2">
            <div className="text-3xl">✅</div>
            <p className="font-black text-sm">{doneMessage}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3 pt-2">
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email Address"
              className="w-full p-4 rounded-xl bg-black/5 border border-black/10 focus:bg-white text-slate-800 outline-none font-bold"
            />
            {/* Invisible Turnstile widget — runs silently in background */}
            <Turnstile
              siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
              onSuccess={setTurnstileToken}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
              options={{ theme: 'light', appearance: 'interaction-only' }}
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full p-4 rounded-xl font-black uppercase tracking-widest bg-black text-white hover:bg-slate-800 will-change-transform"
            >
              {loading ? 'Verifying...' : (data.buttonText || 'Subscribe')}
            </button>
          </form>
        )}
        <p className="text-center text-[10px] opacity-40 uppercase font-black tracking-widest mt-2">{link.title} values your privacy.</p>
      </div>
    </div>
  );
};

/* ============================================================
   MAIN PROFILE VIEW
   ============================================================ */
const ProfileView = ({
  injectedUsername,
  isMockup = false,

  injectedProfile = null,
  injectedLinks = null,
  onCanvasElementDragStart = null,
  selectedCanvasElement = null,

}) => {
  const { username: routeUsername } = useParams();
  const location = useLocation();

  const [rawProfileState, setProfile] = useState(injectedProfile);
  const [rawLinksState, setLinks] = useState(injectedLinks || []);
  const [isSafetyModalOpen, setIsSafetyModalOpen] = useState(false);

  const queryParams = new URLSearchParams(location.search);

  const profile = useMemo(() => {
    if (!rawProfileState) return null;
    return {
      ...rawProfileState,
      full_name: rawProfileState.full_name,
      bio: rawProfileState.bio,
      shop_title: rawProfileState.shop_title,
      shop_description: rawProfileState.shop_description,
      shop_cta: rawProfileState.shop_cta,
      font_family: getRobustFontStack(rawProfileState.font_family),
      title_font_family: getRobustFontStack(rawProfileState.title_font_family),
      bio_font_family: getRobustFontStack(rawProfileState.bio_font_family),
      link_font_family: getRobustFontStack(rawProfileState.link_font_family),
    };
  }, [rawProfileState]);

  const links = useMemo(() => {
    return rawLinksState.map(link => ({
      ...link,
      title: link.title,
      font_family: getRobustFontStack(link.font_family),
    }));
  }, [rawLinksState]);

  // Phase 3: Spatial Analytics & Generative UI
  const generativeLayout = useSpatialTracking(profile?.id, isMockup);
  const [profileStatus, setProfileStatus] = useState(injectedProfile ? 'ready' : 'loading');
  const [activeProfileTab, setActiveProfileTab] = useState('links');
  const [isSubModalOpen, setIsSubModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isUtilitySheetOpen, setIsUtilitySheetOpen] = useState(false);
  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);
  const [checkoutProduct, setCheckoutProduct] = useState(null);
  const [termsLink, setTermsLink] = useState(null);
  const [subscriptionNoticeVisible, setSubscriptionNoticeVisible] = useState(false);
  const [suppressLocationPrompt, setSuppressLocationPrompt] = useState(false);
  const [locationTokenReady, setLocationTokenReady] = useState(false);
  const [locationPromptVisible, setLocationPromptVisible] = useState(false);
  const [locationPromptBusy, setLocationPromptBusy] = useState(false);
  const [headerMediaOpacity, setHeaderMediaOpacity] = useState(1);
  const [headerScrollY, setHeaderScrollY] = useState(0);
  const [isProfileUnlocked, setIsProfileUnlocked] = useState(false);
  const [lockPasswordInput, setLockPasswordInput] = useState('');
  const profileUrl = typeof window !== 'undefined' ? window.location.href : '';
  const profileRef = useRef(profile);
  const linksRef = useRef(links);
  const locationViewTokenRef = useRef(null);


  // Cinematic header: fade media on scroll
  useEffect(() => {
    if (isMockup) return;
    const hasBannerMedia = profile?.banner_media_type && profile.banner_media_type !== 'none';
    const hasCinematic = profile?.header_type === 'cinematic';
    const hasBanner = profile?.header_type === 'banner' && (profile?.banner_image || profile?.banner_video);
    if (!hasBannerMedia && !hasCinematic && !hasBanner) return;

    const fadeHeight = (profile?.header_fade_height || 45) * window.innerHeight / 100;
    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        const opacity = Math.max(0, 1 - (scrollY / (fadeHeight * 0.8)));
        setHeaderMediaOpacity(opacity);
        if (profile?.header_parallax !== false) {
          setHeaderScrollY(scrollY);
        }
        ticking = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMockup, profile?.banner_media_type, profile?.header_type, profile?.header_fade_height, profile?.header_parallax, profile?.banner_image, profile?.banner_video]);

  const openShareSheet = useCallback(() => {
    setIsShareSheetOpen(true);
  }, []);

  const shareProfile = useCallback(async () => {
    if (typeof window === 'undefined') return;
    const shareUrl = profileUrl || window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ url: shareUrl, title: profile?.full_name || `@${profile?.username || 'Linkit'}` });
        return;
      }
      await navigator.clipboard?.writeText(shareUrl);
      toast.success('Profile link copied');
    } catch (error) {
      if (error?.name !== 'AbortError') {
        toast.error('Could not share this profile');
      }
    }
  }, [profile?.full_name, profile?.username, profileUrl]);

  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  useEffect(() => {
    linksRef.current = links;
  }, [links]);

  useEffect(() => {
    if (isMockup) return undefined;
    const openSubscribeModal = () => setIsSubModalOpen(true);
    window.addEventListener('linkit_open_subscribe_modal', openSubscribeModal);
    return () => window.removeEventListener('linkit_open_subscribe_modal', openSubscribeModal);
  }, [isMockup]);

  useEffect(() => {
    if (isMockup) return undefined;
    const params = new URLSearchParams(location.search);
    const trigger = String(params.get('trigger_modal') || params.get('trigger') || '').toLowerCase();
    const shouldOpenLeadModal = ['lead', 'subscribe', 'subscriber', 'true', '1'].includes(trigger) || params.has('lead_offer');
    if (!shouldOpenLeadModal) return undefined;
    const openTimer = window.setTimeout(() => setIsSubModalOpen(true), 0);
    return () => window.clearTimeout(openTimer);
  }, [isMockup, location.search]);

  useEffect(() => {
    if (isMockup || typeof window === 'undefined') return;
    const params = new URLSearchParams(location.search);
    if (params.get('subscribed') !== 'true') return;

    setSubscriptionNoticeVisible(true);
    setSuppressLocationPrompt(true);
    toast.success('You are subscribed', {
      id: 'subscriber-confirmed',
      description: 'Your email is confirmed. The welcome email may take a moment to arrive.',
    });

    params.delete('subscribed');
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
    window.history.replaceState({}, document.title, nextUrl);
  }, [isMockup, location.search]);

  useEffect(() => {
    if (isMockup || !profile?.id || !location.hash) return;
    const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));
    const subscriberEmail = hashParams.get('subscriber_email');
    const subscriberToken = hashParams.get('subscriber_token');
    if (!subscriberEmail || !subscriberToken) return;

    storeSubscriberAccess(profile.id, subscriberEmail.toLowerCase().trim(), subscriberToken);
    window.dispatchEvent(new Event('linkit_gate_unlock'));

    const cleanUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, document.title, cleanUrl);
  }, [isMockup, location.hash, profile?.id]);

  const normalizeLoadedProfile = useCallback((data) => {
    const normalized = { ...(data || {}) };
    if (normalized.tip_jar) {
      ['shop_title', 'shop_description', 'shop_cta', 'shop_title_position', 'bg_video'].forEach(field => {
        if (normalized[field] == null && normalized.tip_jar[field] !== undefined) {
          normalized[field] = normalized.tip_jar[field];
        }
      });
    }
    return { button_radius: 8, social_icon_style: 'mono', ...normalized };
  }, []);

  const maskLinksForVisitor = useCallback((linksData, profileId) => {
    return (linksData || []).map(link => {
      if (link.is_gated) {
        const isSubscribed = Boolean(readSubscriberAccess(profileId).token);
        if (!isSubscribed) {
          return { ...link, url: '#gated', password: undefined };
        }
      }
      return link;
    });
  }, []);

  const applyPublicProfilePayload = useCallback((payload, sourceUsername, { allowRedirect = true } = {}) => {
    const data = payload?.profile;
    if (!data) return false;

    const linksData = payload.links || [];
    const finalProfile = normalizeLoadedProfile(data);
    setProfile(finalProfile);

    if (allowRedirect && !isMockup && data.leap_link_enabled && data.leap_link_url) {
      const url = data.leap_link_url.trim();
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.location.replace(url);
        return true;
      }
    }

    const cacheMeta = {
      version: payload.version,
      profileUpdatedAt: payload.profileUpdatedAt,
      linksUpdatedAt: payload.linksUpdatedAt,
      linksCount: payload.linksCount,
    };
    writePublicProfileCache(data.username || sourceUsername, finalProfile, linksData, cacheMeta);

    if (allowRedirect && !isMockup) {
      const leapLink = linksData.find(l => l.type === 'leap' && l.is_active !== false);
      if (leapLink && leapLink.url && (leapLink.url.startsWith('http://') || leapLink.url.startsWith('https://'))) {
        let shouldLeap = true;
        if (leapLink.is_gated) {
          const isSubscribed = Boolean(readSubscriberAccess(data.id).token);
          if (!isSubscribed) shouldLeap = false;
        }
        if (shouldLeap) {
          window.location.replace(leapLink.url);
          return true;
        }
      }
    }

    setLinks(maskLinksForVisitor(linksData, data.id));
    return true;
  }, [isMockup, maskLinksForVisitor, normalizeLoadedProfile]);

  const applyRealtimeProfileRow = useCallback((row) => {
    if (!row?.id) return;

    setProfile((current) => {
      if (!current || current.id !== row.id) return current;

      const nextProfile = normalizeLoadedProfile({ ...current, ...row });
      const currentLinks = linksRef.current || [];
      writePublicProfileCache(nextProfile.username || current.username, nextProfile, currentLinks, {
        profileUpdatedAt: nextProfile.updated_at || null,
        linksCount: currentLinks.length,
      });
      return nextProfile;
    });
  }, [normalizeLoadedProfile]);

  useEffect(() => {
    if (isMockup || injectedProfile) return undefined;

    return subscribeToLiveProfileUpdates((row) => {
      applyRealtimeProfileRow(row);
    });
  }, [isMockup, injectedProfile, applyRealtimeProfileRow]);

  const applyRealtimeLinkRow = useCallback((payload) => {
    const row = payload?.new?.id ? payload.new : payload?.old;
    if (!row?.id) return;

    const currentProfile = profileRef.current;
    if (!currentProfile?.id || row.profile_id !== currentProfile.id) return;

    setLinks((currentLinks) => {
      let nextLinks;
      if (payload.eventType === 'DELETE') {
        nextLinks = currentLinks.filter(link => link.id !== row.id);
      } else {
        const existingIndex = currentLinks.findIndex(link => link.id === row.id);
        if (existingIndex === -1) {
          nextLinks = [...currentLinks, row];
        } else {
          nextLinks = currentLinks.map(link => link.id === row.id ? { ...link, ...row } : link);
        }
      }

      nextLinks = [...nextLinks].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
      const maskedLinks = maskLinksForVisitor(nextLinks, currentProfile.id);
      writePublicProfileCache(currentProfile.username, currentProfile, maskedLinks, {
        profileUpdatedAt: currentProfile.updated_at || null,
        linksUpdatedAt: row.updated_at || row.created_at || null,
        linksCount: maskedLinks.length,
      });
      return maskedLinks;
    });
  }, [maskLinksForVisitor]);

  // Sync injected data
  useEffect(() => {
    if (injectedProfile) queueMicrotask(() => {
      setProfile(injectedProfile);
      setProfileStatus('ready');
    });
  }, [injectedProfile]);

  useEffect(() => {
    if (injectedLinks) queueMicrotask(() => setLinks(injectedLinks));
  }, [injectedLinks]);

  useEffect(() => {
    if (isMockup || injectedProfile) return;
    const optimisticProfile = readLatestLiveProfileUpdate(injectedUsername || routeUsername);
    if (optimisticProfile) {
      queueMicrotask(() => {
        setProfile(normalizeLoadedProfile(optimisticProfile));
        setProfileStatus('ready');
      });
    }
  }, [routeUsername, injectedUsername, isMockup, injectedProfile, normalizeLoadedProfile]);

  // Fetch from DB — public profile mode
  useEffect(() => {
    if (isMockup || injectedProfile) return;
    const paramUsername = routeUsername || injectedUsername;
    if (!paramUsername) return;

    // Fast-path: show fresh session cache immediately, then refresh from the server-owned endpoint.
    const injected = readInjectedPublicProfile(paramUsername);
    const cached = injected || readPublicProfileCache(paramUsername);
    if (cached) {
      Promise.resolve().then(() => {
        const finalProfile = normalizeLoadedProfile(cached.profile);
        setProfile(finalProfile);
        setProfileStatus('ready');
        setLinks(maskLinksForVisitor(cached.links, finalProfile.id));
        if (injected) {
          writePublicProfileCache(paramUsername, finalProfile, cached.links, {
            version: cached.version,
            profileUpdatedAt: cached.profileUpdatedAt,
            linksUpdatedAt: cached.linksUpdatedAt,
            linksCount: cached.linksCount,
          });
        }
      })
        .catch(() => {
          if (!cached) setProfileStatus('unavailable');
        });
    }


    const refreshPublicProfile = () => fetchPublicProfile(paramUsername)
      .then((payload) => {
        const data = payload.profile;
        const linksData = payload.links || [];
        const error = !data;
        if (data && !error) {
          const finalProfile = normalizeLoadedProfile(data);
          setProfile(finalProfile);
          setProfileStatus('ready');

          // ── Leap Link Redirect ──────────────────────────────────────
          if (!isMockup && data.leap_link_enabled && data.leap_link_url) {
            const url = data.leap_link_url.trim();
            if (url.startsWith('http://') || url.startsWith('https://')) {
              window.location.replace(url);
              return;
            }
          }
          // ─────────────────────────────────────────────────────────────

          writePublicProfileCache(paramUsername, finalProfile, linksData, {
            version: payload.version,
            profileUpdatedAt: payload.profileUpdatedAt,
            linksUpdatedAt: payload.linksUpdatedAt,
            linksCount: payload.linksCount,
          });

          // ── Link-Level Leap Link Logic ──
          if (!isMockup) {
            const leapLink = linksData.find(l => l.type === 'leap' && l.is_active !== false);
            if (leapLink && leapLink.url && (leapLink.url.startsWith('http://') || leapLink.url.startsWith('https://'))) {
              let shouldLeap = true;
              if (leapLink.is_gated) {
                const isSubscribed = Boolean(readSubscriberAccess(data.id).token);
                if (!isSubscribed) shouldLeap = false;
              }
              // Wait! If there's a Terms Portal, they must accept it before leaping? 
              // To keep it simple, Leap Links bypass Terms Portals because you can't show a modal if you are redirecting immediately.
              if (shouldLeap) {
                window.location.replace(leapLink.url);
                return;
              }
            }
          }

          // ── REAL-TIME REVOCATION CHECK ──────────────────────────────
          // If this visitor previously unlocked gated content, silently
          // re-verify their email against the live database. If the creator
          // has removed them, we wipe their localStorage and re-lock the gate.
          const storedAccess = readSubscriberAccess(data.id);
          const storedSub = Boolean(storedAccess.token);
          const storedEmail = storedAccess.email;

          if (storedSub && storedEmail && !isMockup) {
            fetch('/api/v1/verify-sub', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: storedEmail,
                profile_id: data.id,
                subscriber_token: storedAccess.token,
              })
            }).then(verifyRes => {
              if (!verifyRes.ok) {
                // Creator has removed this subscriber — revoke access!
                clearSubscriberAccess(data.id);
                // Re-mask gated links immediately
                setLinks(prev => prev.map(link => {
                  if (link.is_gated) return { ...link, url: '#gated', password: undefined };
                  return link;
                }));
                toast.error('Access Revoked', { description: 'You have been removed from the subscriber list.' });
              }
            }).catch(() => { /* silent fail — network errors don't revoke access */ });
          }
          // ────────────────────────────────────────────────────────────

          // SECURITY: Strip the actual URL from gated links so it can't leak in DevTools.
          // The URL is only revealed after the visitor subscribes (checked via localStorage).
          const safeLinks = linksData.map(link => {
            if (link.is_gated) {
              const isSubscribed = Boolean(readSubscriberAccess(data.id).token);
              if (!isSubscribed) {
                return { ...link, url: '#gated', password: undefined };
              }
            }
            return link;
          });
          setLinks(safeLinks);

        } else {
          if (!cached) setProfileStatus('unavailable');
        }
      })
      .catch(() => {
        if (!cached) setProfileStatus('unavailable');
      });

    const cachedNeedsRedirectCheck = Boolean(
      cached?.profile?.leap_link_enabled ||
      cached?.links?.some(link => link?.type === 'leap' && link?.is_active !== false)
    );

    if (cached?.version && !cachedNeedsRedirectCheck) {
      fetchPublicProfileVersion(paramUsername)
        .then(versionPayload => {
          if (versionPayload.version !== cached.version) {
            refreshPublicProfile();
          }
        })
        .catch(() => refreshPublicProfile());
    } else {
      refreshPublicProfile();
    }
  }, [routeUsername, injectedUsername, isMockup, injectedProfile, normalizeLoadedProfile, maskLinksForVisitor]);

  // Live public preview: open devices refresh themselves when the creator saves.
  useEffect(() => {
    if (isMockup || injectedProfile || !profile?.id || !profile?.username) return;

    let active = true;
    let refreshTimer = null;
    let channel = null;

    const refreshFromServer = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        fetchPublicProfile(profile.username)
          .then((payload) => {
            if (!active) return;
            applyPublicProfilePayload(payload, profile.username, { allowRedirect: false });
          })
          .catch(() => { });
      }, 60);
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshFromServer();
    };

    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('online', refreshFromServer);

    getSupabaseClient()
      .then((supabase) => {
        if (!active) return;
        channel = supabase
          .channel(`public-profile-live:${profile.id}`)
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'profiles',
              filter: `id=eq.${profile.id}`,
            },
            (payload) => {
              applyRealtimeProfileRow(payload.new);
              refreshFromServer();
            }
          )
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'links',
              filter: `profile_id=eq.${profile.id}`,
            },
            (payload) => {
              applyRealtimeLinkRow(payload);
              refreshFromServer();
            }
          )
          .subscribe((status) => {
            if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
              refreshFromServer();
            }
          });
      })
      .catch(() => { });

    return () => {
      active = false;
      window.clearTimeout(refreshTimer);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('online', refreshFromServer);
      if (channel) {
        getSupabaseClient()
          .then((supabase) => supabase.removeChannel(channel))
          .catch(() => { });
      }
    };
  }, [isMockup, injectedProfile, profile?.id, profile?.username, applyPublicProfilePayload, applyRealtimeLinkRow, applyRealtimeProfileRow]);

  // Gate Unlock: Re-fetch links when visitor subscribes to unlock gated content
  useEffect(() => {
    const handleGateUnlock = () => {
      if (!profile?.id || !profile?.username) return;
      fetchPublicProfile(profile.username)
        .then(({ links: linksData = [] }) => {
          if (linksData) {
            // Check if the unlocked link was a Leap Link
            const leapLink = linksData.find(l => l.type === 'leap' && l.is_active !== false);
            if (leapLink && leapLink.url && (leapLink.url.startsWith('http://') || leapLink.url.startsWith('https://'))) {
              window.location.replace(leapLink.url);
              return;
            }
            setLinks(linksData); // Now all URLs are revealed since localStorage is set
          }
        })
        .catch(() => { });
    };
    window.addEventListener('linkit_gate_unlock', handleGateUnlock);
    return () => window.removeEventListener('linkit_gate_unlock', handleGateUnlock);
  }, [profile?.id, profile?.username]);

  // ── SEO & Visibility Logic ──────────────────────────────────────────────────
  // Dynamically updates page metadata based on the creator's dashboard settings.
  useEffect(() => {
    if (!profile || isMockup) return;

    // 1. Dynamic Title handling
    document.title = profile.seo_title || `${profile.full_name || profile.username} | Linkit`;

    // 2. Search Engine Indexing (Robots)
    // If is_searchable is false, we tell crawlers to get lost using the 'noindex' directive.
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.content = profile.is_searchable === false ? 'noindex, nofollow' : 'index, follow';

    // 3. Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    if (profile.seo_description || profile.bio) {
      metaDesc.content = profile.seo_description || profile.bio;
    }
  }, [profile, isMockup]);

  // ── SECURITY: SANITIZATION ENGINE ──────────────────────────────────────────
  const sanitizeId = (id) => {
    if (!id) return '';
    // Strictly alphanumeric + dashes + underscores (prevents script injection)
    return String(id).replace(/[^a-zA-Z0-9\-_]/g, '');
  };

  // ── Marketing Pixels (GA & Meta) ─────────────────────────────────────────────
  // Automatically injects tracking scripts if IDs are provided in settings.
  useEffect(() => {
    if (!profile || isMockup) return;
    const optionalTrackerConsent = localStorage.getItem('linkit_optional_tracker_consent_v1') === 'accepted';
    if (!optionalTrackerConsent) return;

    const nonce = document.querySelector('meta[name="csp-nonce"]')?.content;

    // 1. Google Analytics (GA4) Injection
    const gaId = sanitizeId(profile.ga_measurement_id);
    if (gaId) {
      const script1 = document.createElement('script');
      script1.async = true;
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      if (nonce) script1.setAttribute('nonce', nonce);
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${gaId}', { 'anonymize_ip': true });
      `;
      if (nonce) script2.setAttribute('nonce', nonce);
      document.head.appendChild(script2);
    }

    // 2. Meta Pixel (Facebook) Injection
    const metaId = sanitizeId(profile.meta_pixel_id);
    if (metaId) {
      const script = document.createElement('script');
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${metaId}');
        fbq('track', 'PageView');
      `;
      if (nonce) script.setAttribute('nonce', nonce);
      document.head.appendChild(script);
    }
  }, [profile?.id, isMockup, profile?.ga_measurement_id, profile?.meta_pixel_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load fonts (Custom or Universal)
  useEffect(() => {
    if (!rawProfileState?.font_family && !rawProfileState?.title_font_family && !rawProfileState?.bio_font_family && !rawProfileState?.link_font_family) return;

    // Use rawProfileState (not the processed `profile`) because `profile` has
    // the full fallback stack appended (e.g. "Shrikhand, system-ui, -apple-system, ...")
    // which Google Fonts can't parse. We need the clean name like "Shrikhand".
    [
      rawProfileState.font_family,
      rawProfileState.title_font_family,
      rawProfileState.bio_font_family,
      rawProfileState.link_font_family,
    ].filter(Boolean).forEach(loadGoogleFont);

    // 2. Handle uploaded Custom Fonts
    if (rawProfileState.custom_font_data) {
      const styleId = `custom-font-${String(rawProfileState.font_family || '').replace(/\s+/g, '-').toLowerCase()}`;
      let style = document.getElementById(styleId);
      if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        document.head.appendChild(style);
      }

      // Sanitize the incoming font URL and font-family to avoid script injection
      const fontUrl = sanitizeAbsoluteAssetUrl(rawProfileState.custom_font_data);
      const safeFontFamily = sanitizeFontFamilyName(rawProfileState.font_family);

      if (fontUrl) {
        const quotedUrl = cssString(fontUrl);
        style.textContent = `@font-face { font-family: "${cssString(safeFontFamily)}"; src: url("${quotedUrl}") format("woff2"), url("${quotedUrl}") format("woff"), url("${quotedUrl}") format("truetype"); font-weight: normal; font-style: normal; font-display: swap; }`;
      } else {
        // If the font URL is invalid, ensure we don't inject untrusted content.
        style.textContent = '';
      }
    }
  }, [rawProfileState?.font_family, rawProfileState?.title_font_family, rawProfileState?.bio_font_family, rawProfileState?.link_font_family, rawProfileState?.custom_font_data]);

  // SEO & Analytics Tracking Engine
  useEffect(() => {
    if (!profile || isMockup) return;

    // 1. SEO Configuration
    const pageTitle = profile.seo_title || (profile.full_name ? `${profile.full_name} (@${profile.username}) | Linkit` : `@${profile.username} | Linkit`);
    document.title = pageTitle;

    // Metatags injection
    const updateMeta = (attr, value, content) => {
      let meta = document.querySelector(`meta[${attr}="${value}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, value);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMeta('name', 'description', profile.seo_description || `Check out ${profile.full_name || profile.username}'s links and content on Linkit.`);

    // OpenGraph Tags
    updateMeta('property', 'og:title', pageTitle);
    updateMeta('property', 'og:description', profile.seo_description || profile.bio || 'One link for all your content.');
    updateMeta('property', 'og:image', profile.avatar_url || '');
    updateMeta('property', 'og:url', window.location.href);
    updateMeta('property', 'og:type', 'profile');

    // Twitter Tags
    updateMeta('name', 'twitter:title', pageTitle);
    updateMeta('name', 'twitter:description', profile.seo_description || profile.bio || 'One link for all your content.');
    updateMeta('name', 'twitter:image', profile.avatar_url || '');
    updateMeta('name', 'twitter:card', 'summary_large_image');

    // 2. JSON-LD Person Schema
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": profile.full_name || profile.username,
      "description": profile.bio || "",
      "image": profile.avatar_url || "",
      "url": window.location.href,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": window.location.href
      }
    };

    let script = document.getElementById('json-ld-schema');
    if (!script) {
      script = document.createElement('script');
      script.id = 'json-ld-schema';
      script.type = 'application/ld+json';
      const nonce = document.querySelector('meta[name="csp-nonce"]')?.content;
      if (nonce) script.setAttribute('nonce', nonce);
      document.head.appendChild(script);
    }
    script.text = JSON.stringify(schemaData);

    // 2. Telemetry Tracking (Silent Ping)
    locationViewTokenRef.current = null;
    setLocationTokenReady(false);
    const trackView = async () => {
      const response = await fetch('/api/v1/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...captureAnalyticsContext(),
          profile_id: profile.id,
          event_type: 'view',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (data?.location_token) {
        locationViewTokenRef.current = data.location_token;
        setLocationTokenReady(true);
      }
    };
    trackView().catch(() => { });
  }, [profile, isMockup]);

  useEffect(() => {
    if (!profile?.id || isMockup) return undefined;
    if (suppressLocationPrompt) return undefined;
    if (!locationTokenReady) return undefined;
    if (typeof navigator === 'undefined' || !navigator.geolocation) return undefined;
    if (shouldSkipLocationPrompt(profile.id)) return undefined;

    const promptTimer = window.setTimeout(() => {
      setLocationPromptVisible(true);
    }, 1800);

    return () => window.clearTimeout(promptTimer);
  }, [profile?.id, isMockup, suppressLocationPrompt, locationTokenReady]);

  const dismissLocationPrompt = useCallback(() => {
    if (profile?.id) storeLocationConsentChoice(profile.id, 'dismissed');
    setLocationPromptVisible(false);
  }, [profile?.id]);

  const requestBrowserLocation = useCallback(async () => {
    if (!profile?.id || typeof navigator === 'undefined' || !navigator.geolocation) {
      dismissLocationPrompt();
      return;
    }

    setLocationPromptBusy(true);
    try {
      const position = await requestGeolocationWithFallback();

      if (!locationViewTokenRef.current) {
        storeLocationConsentChoice(profile.id, 'failed');
        setLocationPromptVisible(false);
        toast.info('Location will retry later', {
          description: 'The profile could not prepare a verified analytics event yet.',
        });
        return;
      }

      const locationResponse = await fetch('/api/v1/analytics-location', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...buildAnalyticsPayload(),
          profile_id: profile.id,
          location_token: locationViewTokenRef.current,
          location_consent: true,
          browser_location: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          },
        }),
      });
      const locationResult = await locationResponse.json().catch(() => ({}));
      if (!locationResponse.ok || locationResult?.skipped) {
        throw new Error('Location verification failed.');
      }

      storeLocationConsentChoice(profile.id, 'accepted');
      setLocationPromptVisible(false);
      toast.success('Location shared for analytics', {
        description: 'The creator will see this as a consented browser estimate with an accuracy radius.',
      });
    } catch (error) {
      const denied = error?.code === 1;
      const weakSignal = error?.code === 2 || error?.code === 3;
      storeLocationConsentChoice(profile.id, denied ? 'denied' : 'failed');
      setLocationPromptVisible(false);
      if (denied) {
        return;
      }
      if (weakSignal) {
        toast.info('Location skipped', {
          description: 'Your browser could not provide a precise location right now.',
        });
        return;
      }
      toast.error('Location could not be saved', {
        description: 'The profile still loaded normally. You can continue without sharing location.',
      });
    } finally {
      setLocationPromptBusy(false);
    }
  }, [dismissLocationPrompt, profile?.id]);

  if (!profile && profileStatus === 'loading') return (
    <main className="public-profile-system-state" role="status" aria-live="polite">
      <div className="public-profile-system-card">
        <span className="public-profile-system-mark">L</span>
        <div className="linkit-skeleton h-5 w-36" />
        <div className="linkit-skeleton h-3 w-56" />
        <div className="public-profile-system-links">
          <div className="linkit-skeleton" />
          <div className="linkit-skeleton" />
          <div className="linkit-skeleton" />
        </div>
        <p>Loading this Linkit profile</p>
      </div>
    </main>
  );

  if (!profile) return (
    <main className="public-profile-system-state">
      <div className="public-profile-system-card public-profile-system-unavailable">
        <span className="public-profile-system-mark">L</span>
        <h1>This profile is unavailable</h1>
        <p>It may have been renamed, removed, or temporarily unpublished.</p>
        <div className="public-profile-system-actions">
          <a href="/" className="linkit-control linkit-control-primary linkit-control-md">Visit Linkit</a>
          <button type="button" className="linkit-control linkit-control-secondary linkit-control-md" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    </main>
  );

  // Profile Lock Enforcement (Private Profile Model)
  if (profile.is_locked && !isProfileUnlocked && !isMockup) {
    // Check if they are a known subscriber
    const subAccess = readSubscriberAccess(profile.id);
    if (subAccess.token && subAccess.email) {
      // Optimistically unlock, actual links are still protected on the backend
      setIsProfileUnlocked(true);
      return null; // Will re-render
    }

    return (
      <main className="public-profile-system-state">
        <div className="public-profile-system-card public-profile-system-unavailable">
          <span className="public-profile-system-mark">L</span>
          <h1>Private Profile</h1>
          <p>This profile is private. Enter your email to request access.</p>
          <form className="w-full mt-4 space-y-4" onSubmit={async (e) => {
            e.preventDefault();
            if (!lockPasswordInput || !lockPasswordInput.includes('@')) {
              toast.error('Please enter a valid email');
              return;
            }
            try {
              const email = lockPasswordInput.toLowerCase();
              const client = await getSupabaseClient();

              // 1. Check if they are already in subscriptions
              const { data: subData } = await client
                .from('subscriptions')
                .select('id')
                .eq('profile_id', profile.id)
                .eq('email', email)
                .maybeSingle();

              // 2. Check if they have an approved request
              const { data: reqData } = await client
                .from('profile_access_requests')
                .select('status')
                .eq('profile_id', profile.id)
                .eq('visitor_email', email)
                .maybeSingle();

              if (subData || (reqData && reqData.status === 'approved')) {
                // Save access to localStorage
                localStorage.setItem(`linkit_sub_${profile.id}`, JSON.stringify({
                  token: 'access_granted',
                  email: email
                }));
                setIsProfileUnlocked(true);
                toast.success('Welcome back!');
                return;
              }

              // 3. Otherwise, insert a new request
              const { error } = await client
                .from('profile_access_requests')
                .insert({ profile_id: profile.id, visitor_email: email });

              if (error && error.code === '23505') {
                if (reqData && reqData.status === 'denied') {
                  toast.error('Access Denied', { description: 'The creator has declined your request.' });
                } else {
                  toast.success('Request already sent!', { description: 'We will notify you once approved.' });
                }
              } else if (error) {
                throw error;
              } else {
                toast.success('Access Requested', { description: 'The creator has been notified.' });

                // Trigger push notification to the creator
                const edgeUrl = import.meta.env.VITE_SUPABASE_URL + '/functions/v1/send-push';
                fetch(edgeUrl, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ record: { profile_id: profile.id, visitor_email: email } })
                }).catch(() => { });
              }
              setLockPasswordInput('');
            } catch (err) {
              toast.error('Failed to send request');
            }
          }}>
            <input
              type="email"
              placeholder="your@email.com"
              value={lockPasswordInput}
              onChange={(e) => setLockPasswordInput(e.target.value)}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-slate-900 transition-colors"
            />
            <button type="submit" className="w-full linkit-control linkit-control-primary linkit-control-md">
              Request Access
            </button>
          </form>
        </div>
      </main>
    );
  }

  // Identity Termination Enforcement
  if (profile.is_terminated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6 text-center">
        <div className="max-w-md space-y-6 animate-in fade-in zoom-in-95 duration-700">
          <div className="w-20 h-20 bg-rose-500/10 text-rose-500 rounded-3xl flex items-center justify-center mx-auto border border-rose-500/20">
            <UserX size={40} />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-black text-white tracking-tight">This profile is unavailable</h1>
            <p className="text-slate-500 text-sm leading-relaxed">
              Linkit has disabled this profile following a community standards review.
            </p>
          </div>
          <div className="pt-4">
            <a href="/" className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white rounded-2xl text-xs font-black uppercase tracking-widest transition-all border border-white/10">
              Visit Linkit
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Background style
  const getBgStyle = () => {
    const bg = profile.bg_type;
    if (bg === 'image' && profile.bg_image) {
      return {
        backgroundImage: `url(${profile.bg_image.replace('#contain', '')})`,
        backgroundSize: profile.bg_image.includes('#contain') ? 'contain' : 'cover',
        backgroundPosition: `${profile.bg_pos_x || 50}% ${profile.bg_pos_y || 50}%`,
        backgroundRepeat: profile.bg_image.includes('#contain') ? 'no-repeat' : undefined,
        filter: profile.bg_blur ? `blur(${profile.bg_blur}px)` : undefined,
      };
    }
    if ((bg === 'color' || bg === 'gradient' || bg === 'liquid') && profile.bg_color) return { background: profile.bg_color };
    if (bg === 'liquid') return { background: '#0a0a0a' }; // Default dark for liquid
    return { background: '#F9FAFB' };
  };

  const getCardStyle = (profile) => {
    if (!profile?.card_style || profile.card_style === 'transparent') return null;
    const widthMap = {
      narrow: 'min(420px, 88%)',
      default: 'min(580px, 94%)',
      wide: 'min(720px, 100%)'
    };
    const shadowMap = {
      none: 'shadow-none',
      soft: 'shadow-[0_20px_60px_-10px_rgba(0,0,0,0.15)]',
      hard: 'shadow-[4px_4px_0_0_rgba(0,0,0,0.8)]',
      floating: 'shadow-[0_32px_64px_-12px_rgba(0,0,0,0.35)]',
    };
    return {
      style: {
        maxWidth: widthMap[profile.card_width] || '580px',
        border: profile.card_style === 'outline' ? `2px solid ${profile.title_color || '#000000'}33` : undefined,
      },
      className: shadowMap[profile.card_shadow] || 'shadow-none'
    };
  };

  // ── SAFETY GUARD ────────────────────────────────────────────────────────
  // If profile is not yet loaded, show a minimal loading skeleton to prevent crash
  if (!profile) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center p-8 space-y-6">
        <div className="w-24 h-24 bg-slate-100 rounded-full animate-pulse" />
        <div className="w-48 h-6 bg-slate-100 rounded-xl animate-pulse" />
        <div className="w-full space-y-3">
          <div className="w-full h-12 bg-slate-50 rounded-2xl animate-pulse" />
          <div className="w-full h-12 bg-slate-50 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  // Tabs
  const DEFAULT_TABS = [{ id: 'links', label: 'Links', visible: true, is_default: true }];
  const allTabs = profile.tabs || DEFAULT_TABS;
  const visibleTabs = allTabs.filter(t => t.visible);
  const hasTabs = visibleTabs.length > 1;

  // Layout sections
  const defaultSections = [
    { id: 'header', type: 'header', order: 0, visible: true },
    { id: 'social_icons', type: 'social_icons', order: 1, visible: true },
    { id: 'links', type: 'links', order: 2, visible: true },
  ];
  const sections = [...(profile.layout_sections || defaultSections)].sort((a, b) => a.order - b.order);

  // Card + layout
  const cardData = getCardStyle(profile);
  const hasCard = !!cardData;
  const tabStyle = profile.tab_style || 'pills';
  const contentPaddingMap = { compact: 'px-6 py-8', comfortable: 'px-8 py-10', spacious: 'px-10 py-12' };
  const contentPadding = contentPaddingMap[profile.card_padding] || 'px-6 py-10';

  // Theme-aware icon color (for monochrome icons)
  const iconColor = profile.title_color || '#000000';
  const headerIconColor = profile.header_icon_color || getProfileAccentColor(profile, iconColor);
  const socialIconColor = profile.social_icon_color || getProfileAccentColor(profile, iconColor);
  const footerTextColor = profile.footer_text_color || profile.bio_color || '#666666';
  const accentColor = getProfileAccentColor(profile, iconColor);
  const qrTheme = getProfileQrTheme(profile);

  const getTabLinks = (tabId) => {
    const now = new Date();
    const active = links.filter(l => {
      if (l.is_active === false) return false;
      if (l.start_date && new Date(l.start_date) > now) return false;
      if (l.end_date && new Date(l.end_date) < now) return false;
      return true;
    });

    const sortPinned = (list) => {
      const pinned = list.filter(l => l.is_pinned);
      const unpinned = list.filter(l => !l.is_pinned);
      return [...pinned, ...unpinned];
    };

    if (tabId === 'music') return sortPinned(active.filter(l => ['spotify', 'apple_music', 'yt_music', 'music_embed'].includes(l.type)));
    if (tabId === 'photos') return sortPinned(active.filter(l => l.type === 'image_block'));
    if (tabId === 'links') return sortPinned(active.filter(l => !['spotify', 'apple_music', 'yt_music', 'music_embed', 'image_block'].includes(l.type) || !hasTabs));
    return sortPinned(active);
  };

  // === RENDER HEADER ===

  const getAvatarEl = (iconColor) => {
    const showAvatar = profile.show_avatar !== false;
    if (!showAvatar) return null;
    const avatarSize = profile.avatar_size || 80;
    const avatarShape = profile.avatar_shape || 'circle';
    const headerType = profile.header_type || 'standard';
    const avatarRadiusMap = { circle: '50%', rounded: '16px', squircle: '35%', square: '0px' };
    const sovereignRingColor = pickVisibleRingColor(profile);
    const sovereignRingOuter = pickContrastStroke(profile.card_bg || profile.bg_color || sovereignRingColor);
    const sovereignRingInner = pickContrastStroke(sovereignRingColor);

    return (
      <div className={cn(
        "relative group shrink-0",
        headerType === 'banner' && "-mt-16 md:-mt-20 z-10",
        profile.avatar_ring === 'solid' && 'p-1 bg-white',
        profile.avatar_ring === 'glow' && 'p-[2px] shadow-[0_0_24px_rgba(255,255,255,0.7)] border-2 border-white/50',
        profile.avatar_ring === 'dashed' && 'p-[3px] border-[3px] border-dashed border-white'
      )} style={{ borderRadius: avatarRadiusMap[avatarShape] || '50%' }}>
        {profile.avatar_ring === 'sovereign' && (
          <div
            className="avatar-brutalist-bg transition-all duration-500 will-change-transform"
            style={{
              borderRadius: 'inherit',
              borderColor: sovereignRingColor,
              boxShadow: `0 0 0 1px ${sovereignRingInner}, 0 0 0 3px ${sovereignRingOuter}, 0 0 18px ${sovereignRingColor}`,
            }}
          />
        )}
        <ContentCredentialBadge
          did={profile.c2pa_did || 'did:key:z6MkhaXgBZDvotDkL5257faiztiCEsJUTtcC1R16k8t5yWJv'}
          src={profile.avatar_url}
          alt={profile.full_name}
          className="relative z-10"
          style={{ borderRadius: 'inherit' }}
        >
          <img
            src={profile.avatar_url || 'https://api.dicebear.com/7.x/micah/svg?seed=linkit'}
            alt={profile.full_name}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="border-2 border-slate-900 shadow-xl transition-transform group-hover:scale-105 will-change-transform"
            style={{ width: `${headerType === 'hero' ? avatarSize * 1.5 : avatarSize}px`, height: `${headerType === 'hero' ? avatarSize * 1.5 : avatarSize}px`, borderRadius: 'inherit', objectFit: 'cover', pointerEvents: 'none' }}
          />
        </ContentCredentialBadge>
      </div>
    );
  };

  const getTitleEl = (iconColor) => {
    const showName = profile.show_name !== false;
    const headerType = profile.header_type || 'standard';

    if (!showName || (!profile.title_logo && !profile.full_name)) return null;

    if (profile.title_logo) {
      return (
        <div className="flex flex-col items-center gap-2 relative z-10 w-full mb-2">
          <div className="flex justify-center items-center">
            <img src={profile.title_logo} alt={profile.full_name || 'Logo'} className="h-10 md:h-12 object-contain" />
            {profile.is_verified && profile.show_verified && (
              <div className="ml-2">
                <VerifiedBadge profile={profile} size={headerType === 'hero' ? 28 : 22} onClick={() => setIsSafetyModalOpen(true)} />
              </div>
            )}
          </div>
          <LiveVisitorDot profile={profile} iconColor={iconColor} />
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center gap-2 mb-2">
        <h1 className="leading-tight flex items-center gap-2 anim-misprint" style={{ color: iconColor, fontFamily: profile.title_font_family || profile.font_family || 'inherit', fontWeight: profile.title_font_weight || profile.font_weight || 800, fontSize: profile.title_font_size ? `${headerType === 'hero' ? profile.title_font_size * 1.25 : profile.title_font_size}px` : headerType === 'hero' ? '32px' : '24px', letterSpacing: profile.font_spacing ? `${profile.font_spacing}px` : undefined, textTransform: 'none' }}>
          {profile.full_name}
          {profile.is_verified && profile.show_verified && (
            <VerifiedBadge profile={profile} size={headerType === 'hero' ? 28 : 22} onClick={() => setIsSafetyModalOpen(true)} />
          )}
        </h1>
        <LiveVisitorDot profile={profile} iconColor={iconColor} />
      </div>
    );
  };

  const getUserEl = (iconColor) => {
    const showUsername = profile.show_username !== false;
    if (!showUsername || !profile.username) return null;
    return (
      <p className="opacity-60 -mt-1" style={{ color: iconColor, fontFamily: profile.bio_font_family || profile.font_family || 'inherit', fontWeight: profile.bio_font_weight || profile.font_weight || 400, fontSize: profile.bio_font_size ? `${profile.bio_font_size}px` : '14px', letterSpacing: profile.font_spacing ? `${profile.font_spacing}px` : undefined }}>
        @{profile.username}
      </p>
    );
  };

  const getBioEl = (iconColor) => {
    const showBio = profile.show_bio !== false;
    if (!showBio || !profile.bio) return null;
    return (
      <p className="leading-relaxed max-w-xs opacity-80" style={{ color: profile.bio_color || iconColor, fontFamily: profile.bio_font_family || profile.font_family || 'inherit', fontWeight: profile.bio_font_weight || profile.font_weight || 400, fontSize: profile.bio_font_size ? `${profile.bio_font_size}px` : '14px' }}>
        {profile.bio}
      </p>
    );
  };

  const getBannerEl = () => {
    const headerType = profile.header_type || 'standard';
    if (headerType !== 'banner' || !profile.banner_image || profile.banner_video || (profile.banner_media_type && profile.banner_media_type !== 'none')) return null;
    return (
      <div className="w-full h-40 md:h-56 relative -mt-6 md:-mt-8 rounded-b-[24px] overflow-hidden bg-black/10 shadow-lg mb-4 pointer-events-none">
        <img src={profile.banner_image} loading="eager" decoding="async" fetchPriority="high" className="w-full h-full object-cover" alt="" />
      </div>
    );
  };

  const renderHeader = () => {
    const avatarAlign = profile.avatar_align || 'center';
    const headerType = profile.header_type || 'standard';
    const alignClass = { left: 'items-start text-left', center: 'items-center text-center', right: 'items-end text-right' }[avatarAlign] || 'items-center text-center';
    const iconColor = profile.title_color || '#000000';
    const accentColor = getProfileAccentColor(profile, iconColor);

    const avatarEl = getAvatarEl(iconColor);
    const titleEl = getTitleEl(iconColor);
    const userEl = getUserEl(iconColor);
    const bioEl = getBioEl(iconColor);
    const bannerEl = getBannerEl();

    const nameEl = titleEl;

    if (headerType === 'side') {
      return (
        <div className="flex flex-row items-center gap-5 pb-8 w-full px-1">
          <div className="relative shrink-0">
            {avatarEl}
            {profile.is_verified && profile.show_verified && (
              <div className="absolute -bottom-1 -right-1 z-20 bg-white rounded-full p-0.5 shadow-sm">
                <VerifiedBadge profile={profile} size={18} onClick={() => setIsSafetyModalOpen(true)} />
              </div>
            )}
          </div>
          <div className="flex-1 flex flex-col items-start text-left gap-0.5 min-w-0">
            <div className="flex items-center gap-2 max-w-full">
              {nameEl}
            </div>
            {userEl}
            <div className="w-8 h-0.5 rounded-full my-1 opacity-45" style={{ backgroundColor: accentColor }} />
            {bioEl}
          </div>
        </div>
      );
    }

    if (headerType === 'hero') {
      return (
        <div className="flex flex-col items-center text-center gap-8 pb-12 pt-6 w-full relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-gradient-to-b from-black/5 to-transparent -z-10 pointer-events-none" />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative"
          >
            {avatarEl}
            {profile.is_verified && profile.show_verified && (
              <div className="absolute -bottom-2 -right-2 z-20 bg-white rounded-full p-1 shadow-xl border border-slate-100 scale-110">
                <VerifiedBadge profile={profile} size={24} onClick={() => setIsSafetyModalOpen(true)} />
              </div>
            )}
          </motion.div>
          <div className="flex flex-col items-center gap-3 max-w-sm px-4">
            <div className="space-y-1">
              {nameEl}
              {userEl}
            </div>
            <div className="flex items-center gap-3 w-full justify-center opacity-20">
              <div className="h-px flex-1 bg-slate-900" />
              <Sparkles size={12} className="shrink-0" />
              <div className="h-px flex-1 bg-slate-900" />
            </div>
            {bioEl}
          </div>
        </div>
      );
    }

    if (headerType === 'cinematic') {
      return (
        <div className="flex flex-col items-center text-center gap-6 w-full relative" style={{ paddingTop: `${Math.max(10, (profile.header_fade_height || 45) - 20)}vh` }}>
          <motion.div
            initial={{ scale: 0.85, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10"
          >
            {avatarEl}
            {profile.is_verified && profile.show_verified && (
              <div className="absolute -bottom-2 -right-2 z-20 bg-white rounded-full p-1 shadow-xl border border-slate-100 scale-110">
                <VerifiedBadge profile={profile} size={24} onClick={() => setIsSafetyModalOpen(true)} />
              </div>
            )}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="flex flex-col items-center gap-3 max-w-sm px-4"
          >
            <div className="space-y-1">
              {nameEl}
              {userEl}
            </div>
            <div className="flex items-center gap-3 w-full justify-center opacity-20">
              <div className="h-px flex-1 bg-slate-900" />
              <Sparkles size={12} className="shrink-0" />
              <div className="h-px flex-1 bg-slate-900" />
            </div>
            {bioEl}
          </motion.div>
        </div>
      );
    }

    return (
      <div className={`flex flex-col ${alignClass} gap-3 pb-4 w-full`}>
        {bannerEl}
        {avatarEl}
        {nameEl}
        {userEl}
        {bioEl}
      </div>
    );
  };

  const renderNativeSocialIcons = () => {
    const socialLinks = profile.social_links || {};
    const activeSocials = PLATFORMS.filter(p => socialLinks[p.id]);

    if (activeSocials.length === 0) return null;
    const firstIcon = getSocialIconProps({ profile, platform: activeSocials[0], iconColor: socialIconColor, size: 40 });

    return (
      <div className={cn('flex flex-wrap justify-center pt-1 mb-2', firstIcon.wrapperClassName)}>
        {activeSocials.map(p => {
          const iconProps = getSocialIconProps({ profile, platform: p, iconColor: socialIconColor, size: 40 });
          return (
            <a key={p.id} href={sanitizeUrl(socialLinks[p.id])} target="_blank" rel="noopener noreferrer"
              className={iconProps.className}
              style={iconProps.style}>
              {getSocialIconNode(p, profile)}
            </a>
          );
        })}
      </div>
    );
  };

  // === RENDER TAB BAR ===
  const renderTabBar = () => {
    if (!hasTabs) return null;

    // Style configurations for the Tab Bar container
    const tabContainerStyles = {
      pills: "flex gap-1 p-1 bg-black/10 backdrop-blur-md rounded-full shadow-inner relative z-0",
      underline: "flex gap-2 border-b-2 border-black/5 relative z-0",
      boxed: "flex gap-1 p-1 bg-slate-100 rounded-2xl relative z-0",
      cards: "flex gap-2 relative z-0",
      minimal: "flex gap-4 justify-center relative z-0"
    };

    return (
      <div className={cn("mb-6 overflow-x-auto no-scrollbar", tabContainerStyles[tabStyle] || tabContainerStyles.pills)}>
        {visibleTabs.map(tab => {
          const isActive = activeProfileTab === tab.id;

          // Style configurations for individual Tab Buttons
          const buttonStyles = {
            pills: cn(
              "flex-1 py-3 px-4 text-xs font-black uppercase tracking-widest rounded-full transition-colors text-center whitespace-nowrap relative z-10",
              isActive ? "text-slate-900" : "opacity-60 hover:opacity-100 hover:bg-white/20"
            ),
            underline: cn(
              "flex-1 py-3 px-4 text-xs font-black uppercase tracking-widest transition-all text-center whitespace-nowrap border-b-[3px] relative z-10",
              isActive ? "border-slate-900 text-slate-900" : "border-transparent opacity-40 hover:opacity-80 hover:border-slate-300"
            ),
            boxed: cn(
              "flex-1 py-2.5 px-4 text-xs font-black uppercase tracking-widest rounded-xl transition-colors text-center whitespace-nowrap border-2 relative z-10",
              isActive ? "border-slate-200 text-slate-900" : "border-transparent opacity-50 hover:bg-black/5"
            ),
            cards: cn(
              "flex-1 py-3 px-4 text-xs font-black uppercase tracking-widest rounded-2xl transition-colors text-center whitespace-nowrap border relative z-10",
              isActive ? "border-slate-200 text-slate-900" : "bg-white/50 border-white/20 shadow-sm opacity-60 hover:opacity-100 hover:bg-white"
            ),
            minimal: cn(
              "py-2 text-sm font-black transition-colors whitespace-nowrap relative capitalize z-10",
              isActive ? "text-slate-900 opacity-100" : "opacity-30 hover:opacity-70"
            )
          };

          return (
            <button
              key={tab.id}
              onClick={() => setActiveProfileTab(tab.id)}
              className={buttonStyles[tabStyle] || buttonStyles.pills}
              style={{
                color: ((tabStyle === 'pills' || tabStyle === 'boxed' || tabStyle === 'cards') && isActive) ? undefined : iconColor,
                fontFamily: profile.font_family || 'inherit'
              }}
            >
              {tabStyle === 'pills' && isActive && (
                <motion.div
                  layoutId="publicTabIndicator"
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                  className="absolute inset-0 bg-white rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.1)] -z-10"
                />
              )}
              {tabStyle === 'boxed' && isActive && (
                <motion.div
                  layoutId="publicTabIndicator"
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                  className="absolute inset-0 bg-white rounded-xl shadow-sm -z-10"
                />
              )}
              {tabStyle === 'cards' && isActive && (
                <motion.div
                  layoutId="publicTabIndicator"
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                  className="absolute inset-0 bg-white rounded-2xl shadow-md -z-10 -translate-y-1"
                />
              )}
              {tab.label}
              {/* Minimal style dot indicator */}
              {tabStyle === 'minimal' && isActive && (
                <motion.span layoutId="minimalTabIndicator" transition={{ type: 'spring', stiffness: 500, damping: 35 }} className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accentColor }} />
              )}
            </button>
          );
        })}
      </div>
    );
  };

  // === RENDER CONTENT ===
  const renderContent = () => {
    if (profile.is_canvas_mode) {
      const socialLink = links.find(l => l.type === 'social_icons');
      const iconColor = profile.title_color || '#000000';

      const bannerEl = getBannerEl();
      const avatarEl = getAvatarEl(iconColor);
      const titleEl = getTitleEl(iconColor);
      const bioEl = getBioEl(iconColor);

      const getWrapperClass = (type, id) => {
        const isSelected = selectedCanvasElement?.type === type && selectedCanvasElement?.id === id;
        return `absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto cursor-grab active:cursor-grabbing transition-all rounded-3xl ${isSelected ? 'ring-4 ring-slate-900 shadow-2xl scale-[1.02]' : 'hover:ring-2 hover:ring-slate-400/50'}`;
      };

      return (
        <div className="absolute inset-0 w-full h-full pointer-events-none">
          {/* Banner */}
          {bannerEl && (
            <div className={getWrapperClass('banner', 'banner')}
              style={{ left: `${profile.banner_x ?? 50}%`, top: `${profile.banner_y ?? 15}%`, width: '100%', maxWidth: '400px', zIndex: 10 }}
              onPointerDown={(e) => onCanvasElementDragStart?.(e, 'banner', 'banner')}
            >
              <div className="pointer-events-none">{bannerEl}</div>
            </div>
          )}

          {/* Avatar */}
          {avatarEl && (
            <div className={getWrapperClass('avatar', 'avatar')}
              style={{ left: `${profile.avatar_x ?? 50}%`, top: `${profile.avatar_y ?? 15}%`, zIndex: 20 }}
              onPointerDown={(e) => onCanvasElementDragStart?.(e, 'avatar', 'avatar')}
            >
              <div className="pointer-events-none">{avatarEl}</div>
            </div>
          )}

          {/* Title */}
          {titleEl && (
            <div className={getWrapperClass('title', 'title')}
              style={{ left: `${profile.title_x ?? 50}%`, top: `${profile.title_y ?? 28}%`, width: '100%', maxWidth: '280px', zIndex: 20 }}
              onPointerDown={(e) => onCanvasElementDragStart?.(e, 'title', 'title')}
            >
              <div className="flex flex-col items-center justify-center text-center pointer-events-none">
                {titleEl}
                {getUserEl(iconColor)}
              </div>
            </div>
          )}

          {/* Bio */}
          {bioEl && (
            <div className={getWrapperClass('bio', 'bio')}
              style={{ left: `${profile.bio_x ?? 50}%`, top: `${profile.bio_y ?? 38}%`, width: '100%', maxWidth: '280px', zIndex: 20 }}
              onPointerDown={(e) => onCanvasElementDragStart?.(e, 'bio', 'bio')}
            >
              <div className="flex flex-col items-center justify-center text-center pointer-events-none">
                {bioEl}
              </div>
            </div>
          )}

          {/* Social Icons */}
          {sections.find(s => s.type === 'social_icons' && s.visible) && (
            <div className={getWrapperClass(socialLink ? 'link' : 'socials', socialLink ? socialLink.id : 'socials')}
              style={{ left: `${socialLink ? (socialLink.x_pos ?? 50) : (profile.socials_x ?? 50)}%`, top: `${socialLink ? (socialLink.y_pos ?? 25) : (profile.socials_y ?? 45)}%`, width: `${socialLink?.width_pct ?? 80}%`, zIndex: socialLink?.z_index ?? 20 }}
              onPointerDown={(e) => onCanvasElementDragStart?.(e, socialLink ? 'link' : 'socials', socialLink ? socialLink.id : 'socials')}
            >
              <div className="pointer-events-none">{socialLink ? <LinkBlock link={socialLink} profile={profile} iconColor={iconColor} setTermsLink={setTermsLink} /> : renderNativeSocialIcons()}</div>
            </div>
          )}

          {/* Links */}
          {getTabLinks(activeProfileTab).map((link, index) => {
            const isUnpositioned = Number(link.x_pos) === 10 && Number(link.y_pos) === 10;
            const x = isUnpositioned ? 50 : (link.x_pos ?? 50);
            const y = isUnpositioned ? 35 + (index * 12) : (link.y_pos ?? 50);
            return (
              <div key={link.id} className={getWrapperClass('link', link.id)}
                onPointerDown={(e) => onCanvasElementDragStart?.(e, 'link', link.id)}
                style={{ left: `${x}%`, top: `${y}%`, width: `${link.width_pct ?? 80}%`, zIndex: link.z_index ?? 10 }}>
                <div className="pointer-events-none w-full"><LinkBlock link={link} profile={profile} iconColor={iconColor} setTermsLink={setTermsLink} /></div>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="space-y-4 max-w-md mx-auto w-full">
        {sections.filter(s => s.visible).map(section => {
          if (section.type === 'header') {
            return <div key={section.id} className="mb-2">{renderHeader()}</div>;
          }

          if (section.type === 'social_icons') {
            const socialLink = links.find(l => l.type === 'social_icons');
            return (
              <div key={section.id}>
                {socialLink ? <LinkBlock link={socialLink} profile={profile} iconColor={iconColor} setTermsLink={setTermsLink} /> : renderNativeSocialIcons()}
              </div>
            );
          }

          if (section.type === 'links') {
            return (
              <div key={section.id} className="space-y-4 w-full">
                {hasTabs && renderTabBar()}

                {/* Affiliate disclaimer */}
                {profile.show_affiliate_disclaimer && activeProfileTab === 'shop' && (
                  <p className="text-center text-xs opacity-40" style={{ color: iconColor }}>Products may contain affiliate links</p>
                )}

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeProfileTab}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -15 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    className="space-y-3"
                  >
                    {activeProfileTab === 'shop' ? (
                      <ShopView profile={profile} onProductClick={setCheckoutProduct} />
                    ) : activeProfileTab === 'about' ? (
                      <div className="py-6 space-y-6">
                        {(profile.about_content || profile.bio) && (
                          <p className="text-sm leading-relaxed whitespace-pre-wrap px-2" style={{ color: profile.bio_color || iconColor }}>
                            {profile.about_content || profile.bio}
                          </p>
                        )}
                      </div>
                    ) : (
                      <>
                        {getTabLinks(activeProfileTab).map(link => (
                          <motion.div
                            key={link.id}
                            className={link.animation ? `link-anim-${link.animation}` : ''}
                          >
                            <LinkBlock link={link} profile={profile} iconColor={iconColor} setTermsLink={setTermsLink} />
                          </motion.div>
                        ))}
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            );
          }

          if (section.type === 'shop') {
            // Avoid double rendering if shop is displayed inside links via tab
            if (hasTabs && activeProfileTab === 'shop') return null;
            return <div key={section.id} className="mb-4"><ShopView profile={profile} onProductClick={setCheckoutProduct} /></div>;
          }

          return null;
        })}
      </div>
    );
  };

  // When rendered inside the dashboard phone mockup, use `absolute` so the background
  // stays inside the frame instead of painting over the entire browser window.
  const bgPos = isMockup ? 'absolute' : 'fixed';

  const hasHeaderMediaFade = ((profile.banner_media_type && profile.banner_media_type !== 'none') ||
    ((profile.header_type === 'cinematic' || profile.header_type === 'banner') && (profile.banner_image || profile.banner_video)));

  const headerMediaFadeElement = hasHeaderMediaFade ? (
    <div
      className="absolute top-0 left-0 right-0 pointer-events-none z-0 overflow-hidden header-cinematic-media"
      style={{
        height: `${profile.header_fade_height || 45}vh`,
        opacity: headerMediaOpacity,
        maskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
        borderTopLeftRadius: hasCard ? 'inherit' : 0,
        borderTopRightRadius: hasCard ? 'inherit' : 0,
      }}
    >
      {/* Media content */}
      {(profile.banner_media_type === 'video' || (!profile.banner_media_type && profile.banner_video)) && profile.banner_video ? (
        <video
          src={profile.banner_video}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
          style={profile.header_parallax !== false && !hasCard ? { transform: `translateY(${headerScrollY * 0.4}px)` } : undefined}
        />
      ) : profile.banner_image ? (
        <img
          src={profile.banner_image}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover"
          style={profile.header_parallax !== false && !hasCard ? { transform: `translateY(${headerScrollY * 0.4}px)` } : undefined}
          alt=""
        />
      ) : null}

      {/* Darkening overlay */}
      <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${profile.header_overlay_opacity || 0.3})` }} />
    </div>
  ) : null;

  return (
    <div className={cn("relative z-0 min-h-full", isMockup ? "" : "")} style={{ fontFamily: profile.font_family || undefined }}>
      {!isMockup && <LiveCanvas profileId={profile?.id} />}
      {!isMockup && <GenerativeCanvas schema={generativeLayout} />}
      {/* 
          PRESSURE_ITERATIONS: 10,
          SECURITY HONEY-TOKEN MINEFIELD:
          This link is invisible to humans but visible to automated scrapers.
          Any interaction with it triggers an immediate IP-based ban.
      */}
      <a
        href="/api/trap?action=scraper_caught"
        style={{ display: 'none' }}
        aria-hidden="true"
        tabIndex="-1"
      >
        Click here for developer data
      </a>
      {/* Page background */}
      <div className={cn(bgPos, "inset-0 pointer-events-none -z-10")} style={getBgStyle()} />

      {/* Video background */}
      {profile.bg_type === 'video' && profile.bg_video && (
        <video
          src={profile.bg_video.replace('#contain', '')}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className={cn(`${bgPos} inset-0 w-full h-full pointer-events-none -z-10`, profile.bg_video.includes('#contain') ? "object-contain bg-black" : "object-cover")}
          style={{ objectPosition: `${profile.bg_pos_x ?? 50}% ${profile.bg_pos_y ?? 50}%` }}
        />
      )}

      {/* Advanced Liquid Canvas (Edgecore layer) — Disabled in Mockup for performance */}
      {profile.bg_type === 'liquid' && !isMockup && (
        <React.Suspense fallback={null}>
          <FluidCanvas
            key={`${profile.liquid_color_scheme || profile.liquid_colors || 'vibrant'}-${profile.liquid_intensity || 0.5}-${profile.liquid_custom_color || '#FF0055'}`}
            intensity={profile.liquid_intensity || 0.5}
            colorScheme={profile.liquid_color_scheme || profile.liquid_colors || "vibrant"}
            customColor={profile.liquid_custom_color || "#FF0055"}
            positionClass={isMockup ? 'absolute' : 'fixed'}
          />
        </React.Suspense>
      )}

      {/* Surface Texture Layer — isolated to its own GPU layer to prevent
           mix-blend-mode from forcing full-viewport recomposition on every animation frame */}
      {profile.bg_type !== 'liquid' && profile.bg_texture && profile.bg_texture !== 'none' && (
        <div className={`${bgPos} inset-0 pointer-events-none -z-10`}
          style={{
            backgroundImage: profile.bg_texture === 'noise' ? `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")` :
              profile.bg_texture === 'grid' ? `linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)` :
                profile.bg_texture === 'dots' ? `radial-gradient(circle, #ffffff 1.5px, transparent 1.5px)` : 'none',
            backgroundSize: profile.bg_texture === 'noise' ? '150px 150px' : profile.bg_texture === 'grid' ? '32px 32px' : profile.bg_texture === 'dots' ? '20px 20px' : 'auto',
            backgroundPosition: '0 0',
            opacity: profile.texture_opacity || 0.3,
            mixBlendMode: profile.bg_texture === 'noise' ? 'overlay' : 'difference',
            // GPU isolation: prevents this blend-mode layer from forcing
            // recomposition of ALL sibling layers (link buttons, cards, etc.)
            isolation: 'isolate',
            willChange: 'opacity',
            contain: 'strict',
          }}
        />
      )}

      {/* Motion canvas */}
      <MotionCanvas
        type={profile.animation_type}
        color={profile.animation_color || '#FFFFFF'}
        intensity={profile.animation_intensity || 5}
        speed={profile.animation_speed || 1}
        density={profile.animation_density || 1}
        customData={profile.animation_custom_data}
      />

      {/* Content */}
      <div className={cn("public-profile-shell relative z-10 flex min-h-[100dvh] flex-col", hasCard ? "px-4 py-8" : "px-0 py-0 pb-12")}>
        {/* Header Media Fade Layer — renders at content root if there is no card */}
        {!hasCard && headerMediaFadeElement}

        {subscriptionNoticeVisible && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="fixed inset-x-3 top-4 z-[75] mx-auto max-w-md rounded-3xl border border-emerald-200 bg-white/95 p-4 text-left shadow-2xl backdrop-blur-xl sm:top-5"
          >
            <div className="flex gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white">
                <CheckCircle size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-black text-slate-950">You are subscribed</p>
                <p className="mt-1 text-xs font-bold leading-relaxed text-slate-600">
                  Your email is confirmed. The welcome email may take a moment to arrive, but your subscriber access is active now.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSubscriptionNoticeVisible(false)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Dismiss subscription confirmation"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}
        {hasCard ? (
          <div
            className={cn(
              "public-profile-card relative mx-auto my-8 w-full flex-none bg-transparent transition-all sm:my-10",
              "rounded-[var(--card-radius)]",
              cardData.className
            )}
            style={{
              ...cardData.style,
              '--card-radius': `${profile.card_radius ?? 24}px`
            }}
          >
            {/* Card background media & color layer */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-[inherit]">
              {/* Media Layer (Image/Video) */}
              {profile.card_bg_type === 'image' && profile.card_bg_image && (
                <img src={profile.card_bg_image.replace('#contain', '')} loading="eager" decoding="async" fetchPriority="high" className={cn("absolute inset-0 w-full h-full", profile.card_bg_image.includes('#contain') ? "object-contain bg-black" : "object-cover")} alt="" />
              )}
              {profile.card_bg_type === 'video' && profile.card_bg_video && (
                <video src={profile.card_bg_video.replace('#contain', '')} autoPlay muted loop playsInline preload="metadata" className={cn("absolute inset-0 w-full h-full", profile.card_bg_video.includes('#contain') ? "object-contain bg-black" : "object-cover")} />
              )}

              {/* Base Color / Gradient Layer */}
              <div className="absolute inset-0" style={{
                background: profile.card_style === 'outline' ? 'transparent' : profile.card_style === 'glass' && !profile.card_bg ? 'rgba(255,255,255,0.05)' : (profile.card_bg || 'rgba(255,255,255,0.92)'),
                opacity: profile.card_style === 'outline' ? 1 : profile.card_style === 'glass' ? (profile.card_opacity ?? 0.15) : (profile.card_opacity ?? 1),
              }} />

              {/* Glass / Overlay Layer */}
              <div className="absolute inset-0" style={{
                backdropFilter: profile.card_style === 'glass' ? `blur(${profile.card_blur ?? 16}px)` : undefined,
                WebkitBackdropFilter: profile.card_style === 'glass' ? `blur(${profile.card_blur ?? 16}px)` : undefined,
              }} />

              {/* Header Media Fade — renders inside the card if there is a card */}
              {hasCard && headerMediaFadeElement}
            </div>

            {/* Header action bar */}
            <div className="public-profile-card-actions relative mb-2 flex items-center justify-between px-6 pt-6">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsUtilitySheetOpen(true)}
                  className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                  style={{ background: `${accentColor}20` }}
                  aria-label="Open Linkit profile tools"
                >
                  <span className="text-sm font-black leading-none" style={{ color: accentColor, opacity: 0.55 }}>L</span>
                </button>
              </div>
              <div className="flex items-center gap-2">
                {profile.contact_email && (
                  <button
                    onClick={() => setIsContactModalOpen(true)}
                    className={cn(
                      "rounded-full bg-black/8 hover:bg-black/12 transition-all flex items-center justify-center",
                      !isMockup ? "w-9 h-9 sm:w-auto sm:h-auto sm:px-3 sm:py-2 sm:gap-1.5" : "w-9 h-9"
                    )}
                    style={{ color: headerIconColor }}
                    aria-label="Send Message"
                  >
                    <MessageCircle size={15} />
                    {!isMockup && <span className="hidden sm:inline text-xs font-bold">Message</span>}
                  </button>
                )}
                {profile.show_notify_btn && (
                  <button
                    onClick={() => setIsSubModalOpen(true)}
                    className="w-9 h-9 rounded-full bg-black/8 hover:bg-black/12 transition-all flex items-center justify-center"
                    style={{ color: headerIconColor }}
                  >
                    <Bell size={15} />
                  </button>
                )}
                <button
                  className="w-9 h-9 rounded-full bg-black/8 hover:bg-black/12 transition-all flex items-center justify-center"
                  style={{ color: headerIconColor }}
                  onClick={openShareSheet}
                >
                  <Share2 size={15} />
                </button>
                <button
                  className="w-9 h-9 rounded-full bg-black/8 hover:bg-black/12 transition-all flex items-center justify-center"
                  style={{ color: headerIconColor }}
                  onClick={() => setIsReportModalOpen(true)}
                  title="Report Profile"
                >
                  <Flag size={14} />
                </button>
              </div>
            </div>
            {/* Card content */}
            <div className={`relative ${contentPadding} pt-2`}>
              {renderContent()}
            </div>
            <DecorationLayer decorations={profile.profile_decorations} profileId={profile.id} />
          </div>
        ) : (
          <div className="public-profile-no-card relative px-5 py-8">
            {/* Floating header actions for no-card profiles */}
            <div className="flex justify-end gap-2 mb-4 max-w-md mx-auto">
              {profile.contact_email && (
                <button
                  onClick={() => setIsContactModalOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold backdrop-blur-sm transition-all hover:brightness-110"
                  style={{
                    backgroundColor: profile.utility_button_bg_color || 'rgba(255,255,255,0.15)',
                    color: profile.utility_button_color || 'white'
                  }}>
                  <MessageCircle size={13} />
                  <span>Message</span>
                </button>
              )}
              {profile.show_notify_btn && (
                <button
                  onClick={() => setIsSubModalOpen(true)}
                  className="w-9 h-9 rounded-full backdrop-blur-sm transition-all flex items-center justify-center hover:brightness-110"
                  style={{
                    backgroundColor: profile.utility_button_bg_color || 'rgba(255,255,255,0.15)',
                    color: profile.utility_button_color || 'white'
                  }}
                >
                  <Bell size={15} />
                </button>
              )}
              <button
                className="w-9 h-9 rounded-full backdrop-blur-sm transition-all flex items-center justify-center hover:brightness-110"
                style={{
                  backgroundColor: profile.utility_button_bg_color || 'rgba(255,255,255,0.15)',
                  color: profile.utility_button_color || 'white'
                }}
                onClick={openShareSheet}
              >
                <Share2 size={15} />
              </button>
            </div>
            {renderContent()}
            <DecorationLayer decorations={profile.profile_decorations} profileId={profile.id} />
          </div>
        )}

        {profile.show_affiliate_disclaimer && profile.affiliate_disclaimer_text && (
          <div className="px-5 py-6 pb-2 text-center max-w-lg mx-auto">
            <p className="text-[10px] font-bold text-slate-400 opacity-60 leading-relaxed uppercase tracking-wide">
              {profile.affiliate_disclaimer_text}
            </p>
          </div>
        )}

        {profile.show_branding_footer !== false && (
          <ProfileFooter
            color={footerTextColor}
            opacity={profile.branding_footer_opacity}
            username={profile.username}
            onOpenUtility={() => setIsUtilitySheetOpen(true)}
          />
        )}

        {/* Tarpit Honeypot: Invisible link to trap scraping bots */}
        <a href="/api/trap" aria-hidden="true" tabIndex="-1" className="sr-only" style={{ display: 'none' }}>Admin Login</a>
      </div>

      {/* Floating Tip Button */}
      {profile.tip_jar?.enabled && profile.tip_jar?.url && (
        <a
          href={sanitizeUrl(profile.tip_jar.url)}
          target="_blank"
          rel="noopener noreferrer"
          className={cn("fixed bottom-6 right-6 z-[60] flex items-center gap-2 rounded-full px-5 py-3 font-black uppercase tracking-widest text-sm shadow-2xl transition-transform hover:scale-105 active:scale-95", isMockup ? "bottom-6" : "sm:bottom-6")}
          style={{
            backgroundColor: profile.button_color || '#0F172A',
            color: profile.link_text_color || '#FFFFFF',
          }}
        >
          {profile.tip_jar.text || 'Support Me'}
        </a>
      )}

      {/* Floating "View on Mobile" QR panel — desktop only */}
      {!isMockup && (
        <div
          className="fixed bottom-10 right-10 z-50 hidden w-[154px] flex-col items-center gap-2 rounded-2xl border bg-white/95 p-3 text-center shadow-2xl backdrop-blur-md lg:flex"
          style={{
            borderColor: qrTheme.accent,
            boxShadow: `0 18px 50px -20px ${qrTheme.shadow}`,
          }}
        >
          <p
            className="text-[9px] font-black uppercase leading-none tracking-widest"
            style={{ color: qrTheme.accent }}
          >
            View on mobile
          </p>
          <div className="rounded-xl bg-white p-2 shadow-inner ring-1 ring-slate-900/10">
            <QRCodeSVG
              value={profileUrl || window.location.href}
              size={118}
              bgColor={qrTheme.background}
              fgColor={qrTheme.foreground}
              level="Q"
              includeMargin
            />
          </div>
          <p className="max-w-full truncate text-[9px] font-bold leading-none text-slate-500">
            @{profile.username}
          </p>
        </div>
      )}
      {!isMockup && locationPromptVisible && (
        <LocationConsentPrompt
          profile={profile}
          busy={locationPromptBusy}
          onAccept={requestBrowserLocation}
          onDismiss={dismissLocationPrompt}
        />
      )}
      <React.Suspense fallback={null}>
        {isSubModalOpen && (
          <SubscriptionModal
            isOpen={isSubModalOpen}
            onClose={() => setIsSubModalOpen(false)}
            profileId={profile.id}
            username={profile.username}
            profile={profile}
          />
        )}
        {isContactModalOpen && (
          <ContactModal
            isOpen={isContactModalOpen}
            onClose={() => setIsContactModalOpen(false)}
            profile={profile}
          />
        )}
        {!!termsLink && (
          <TermsPortalModal
            isOpen={!!termsLink}
            onClose={() => setTermsLink(null)}
            title={termsLink?.title}
            termsText={termsLink?.terms_text}
            onAccept={() => {
              if (termsLink) {
                window.open(getPublicLinkHref(termsLink), '_blank', 'noopener,noreferrer');
                if (!canUseAttributionRoute(termsLink)) {
                  fetch('/api/v1/track', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      ...buildAnalyticsPayload(),
                      link_id: termsLink.id,
                      profile_id: profile?.id,
                      event_type: 'click',
                      platform: 'web',
                      destination_platform: detectAnalyticsPlatform(termsLink.url),
                    }),
                  }).catch((e) => console.error(e));
                }
              }
            }}
          />
        )}
      </React.Suspense>
      {checkoutProduct && (
        <React.Suspense fallback={<div />}>
          <CheckoutModal product={profile.shop_products?.find(p => p.id === checkoutProduct.id) || checkoutProduct} profile={profile} onClose={() => setCheckoutProduct(null)} />
        </React.Suspense>
      )}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        profile={profile}
      />
      <ProfileUtilitySheet
        isOpen={isUtilitySheetOpen}
        onClose={() => setIsUtilitySheetOpen(false)}
        profile={profile}
        profileUrl={profileUrl}
        qrTheme={qrTheme}
        onSubscribe={() => {
          setIsUtilitySheetOpen(false);
          setIsSubModalOpen(true);
        }}
        onMessage={() => {
          setIsUtilitySheetOpen(false);
          setIsContactModalOpen(true);
        }}
        onShare={() => {
          setIsUtilitySheetOpen(false);
          setIsShareSheetOpen(true);
        }}
        onReport={() => {
          setIsUtilitySheetOpen(false);
          setIsReportModalOpen(true);
        }}
      />
      <ShareProfileSheet
        isOpen={isShareSheetOpen}
        onClose={() => setIsShareSheetOpen(false)}
        profile={profile}
        profileUrl={profileUrl}
        qrTheme={qrTheme}
        onNativeShare={shareProfile}
        onSubscribe={() => {
          setIsShareSheetOpen(false);
          setIsSubModalOpen(true);
        }}
        onMessage={() => {
          setIsShareSheetOpen(false);
          setIsContactModalOpen(true);
        }}
      />

      {profile?.is_verified && !isMockup && (
        <React.Suspense fallback={null}>
          <SafetyNumberModal
            profile={profile}
            isOpen={isSafetyModalOpen}
            onClose={() => setIsSafetyModalOpen(false)}
          />
        </React.Suspense>
      )}
    </div>
  );
};

/* ============================================================
   SHARE PROFILE SHEET
   ============================================================ */
const ShareProfileSheet = ({
  isOpen,
  onClose,
  profile,
  profileUrl,
  qrTheme,
  onNativeShare,
  onSubscribe,
  onMessage,
}) => {
  const qrRef = useRef(null);
  const accent = normalizeHexColor(getProfileAccentColor(profile)) || '#0F172A';
  const ink = normalizeHexColor(profile?.title_color) || '#0F172A';
  const displayName = profile?.full_name || `@${profile?.username || 'creator'}`;
  const handle = profile?.username ? `@${profile.username}` : 'Linkit profile';
  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') return profileUrl || '';
    try {
      const url = new URL(profileUrl || window.location.href);
      url.searchParams.set('utm_source', 'visitor_share');
      url.searchParams.set('utm_medium', 'profile_share');
      url.searchParams.set('utm_campaign', profile?.username || 'public_profile');
      return url.href;
    } catch {
      return profileUrl || window.location.href;
    }
  }, [profile?.username, profileUrl]);
  const shareText = `Open ${displayName}'s Linkit profile`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);
  const sharePlatforms = useMemo(() => ({
    instagram: PLATFORMS.find((platform) => platform.id === 'instagram'),
    whatsapp: PLATFORMS.find((platform) => platform.id === 'whatsapp'),
    x: PLATFORMS.find((platform) => platform.id === 'twitter'),
    linkedin: PLATFORMS.find((platform) => platform.id === 'linkedin'),
    facebook: PLATFORMS.find((platform) => platform.id === 'facebook'),
  }), []);

  const copyShareUrl = async () => {
    await navigator.clipboard?.writeText(shareUrl);
    toast.success('Share link copied', { description: 'Campaign tags were added for analytics.' });
  };

  const openShareDestination = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const shareToInstagram = async () => {
    await navigator.clipboard?.writeText(shareUrl);
    toast.success('Link copied for Instagram', {
      description: 'Paste it into an Instagram story, DM, bio, or caption.',
    });
    openShareDestination('https://www.instagram.com/');
  };

  const downloadQr = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const source = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `linkit-${profile?.username || 'profile'}-share-qr.svg`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const destinations = [
    { label: 'Copy', icon: <Copy size={20} />, onClick: copyShareUrl },
    { label: 'Instagram', icon: sharePlatforms.instagram?.svg, onClick: shareToInstagram, background: sharePlatforms.instagram?.gradient || sharePlatforms.instagram?.color || '#E1306C' },
    { label: 'WhatsApp', icon: sharePlatforms.whatsapp?.svg, onClick: () => openShareDestination(`https://wa.me/?text=${encodedText}%20${encodedUrl}`), color: sharePlatforms.whatsapp?.color || '#25D366' },
    { label: 'X', icon: sharePlatforms.x?.svg, onClick: () => openShareDestination(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`), color: sharePlatforms.x?.color || '#000000' },
    { label: 'LinkedIn', icon: sharePlatforms.linkedin?.svg, onClick: () => openShareDestination(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`), color: sharePlatforms.linkedin?.color || '#0A66C2' },
    { label: 'Facebook', icon: sharePlatforms.facebook?.svg, onClick: () => openShareDestination(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`), color: sharePlatforms.facebook?.color || '#1877F2' },
    { label: 'Email', icon: <Mail size={20} />, onClick: () => { window.location.href = `mailto:?subject=${encodedText}&body=${encodedUrl}`; } },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[595] flex items-end justify-center bg-slate-950/60 px-3 pb-3 pt-10 backdrop-blur-xl sm:items-center sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 28, scale: 0.98 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 28, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 420, damping: 34 }}
            onClick={(event) => event.stopPropagation()}
            className="profile-share-sheet no-scrollbar max-h-[calc(100dvh-1.5rem)] w-full max-w-xl overflow-y-auto rounded-[2rem] border border-slate-200 bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between px-5 py-4 sm:px-6">
              <h3 className="text-lg font-black tracking-tight text-slate-950">Share Profile</h3>
              <button
                type="button"
                onClick={onClose}
                className="flex h-10 w-10 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-950"
                aria-label="Close share profile"
              >
                <X size={19} />
              </button>
            </div>

            <div className="px-5 pb-5 sm:px-6">
              <div
                className="profile-share-preview relative overflow-hidden rounded-[1.5rem] p-6 text-center"
                style={{
                  background: `linear-gradient(135deg, ${hexToRgba(accent, 0.92)}, ${hexToRgba(ink, 0.88)})`,
                  color: '#FFFFFF',
                }}
              >
                <div className="mx-auto h-20 w-20 overflow-hidden rounded-full border-4 border-white/80 bg-white/20 shadow-xl">
                  {profile?.avatar_url ? (
                    <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-2xl font-black">
                      {(displayName || 'L').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <p className="truncate text-2xl font-black tracking-tight">{displayName}</p>
                  {profile?.is_verified && profile?.show_verified && <CheckCircle size={20} className="shrink-0" />}
                </div>
                <p className="mt-1 text-sm font-black opacity-80">{handle}</p>
              </div>
            </div>

            <div className="profile-share-destinations no-scrollbar grid grid-cols-4 gap-3 px-5 pb-5 sm:flex sm:gap-4 sm:overflow-x-auto sm:px-6">
              {destinations.map((destination) => (
                <button
                  key={destination.label}
                  type="button"
                  onClick={destination.onClick}
                  className="flex min-w-0 flex-col items-center gap-2 text-center text-xs font-bold text-slate-700 sm:min-w-[72px]"
                >
                  <span
                    className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-lg font-black text-slate-950"
                    style={destination.background
                      ? { background: destination.background, color: '#FFFFFF' }
                      : destination.color
                        ? { backgroundColor: destination.color, color: '#FFFFFF' }
                        : undefined}
                  >
                    {destination.icon}
                  </span>
                  <span className="max-w-[72px] leading-tight">{destination.label}</span>
                </button>
              ))}
            </div>

            <div className="border-y border-slate-100 px-5 py-5 sm:px-6">
              <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-center">
                <div ref={qrRef} className="w-fit rounded-2xl bg-white p-3 shadow-inner ring-1 ring-slate-900/10">
                  <QRCodeSVG
                    value={shareUrl}
                    size={132}
                    bgColor={qrTheme?.background || '#FFFFFF'}
                    fgColor={qrTheme?.foreground || '#111827'}
                    level="Q"
                    includeMargin
                  />
                </div>
                <div className="space-y-3">
                  <p className="text-sm font-black text-slate-950">Campaign share link</p>
                  <p className="break-all rounded-2xl bg-slate-50 px-4 py-3 text-xs font-bold text-slate-500">{shareUrl}</p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={copyShareUrl} className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-3 text-xs font-black uppercase tracking-widest text-white">
                      <Copy size={14} /> Copy Link
                    </button>
                    <button type="button" onClick={downloadQr} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-700">
                      <Download size={14} /> Download QR
                    </button>
                    {navigator.share && (
                      <button type="button" onClick={onNativeShare} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-700">
                        <Share2 size={14} /> Native Share
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3 px-5 py-5 sm:px-6">
              {profile?.contact_email && (
                <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-100 px-4 py-3">
                  <div>
                    <p className="text-sm font-black text-slate-950">Send {profile?.username || 'this creator'} a message</p>
                    <p className="text-xs font-bold text-slate-500">Start a private conversation from this profile.</p>
                  </div>
                  <button type="button" onClick={onMessage} className="rounded-xl border border-slate-200 px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-800">
                    Message
                  </button>
                </div>
              )}
              {profile?.show_notify_btn && (
                <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-100 px-4 py-3">
                  <div>
                    <p className="text-sm font-black text-slate-950">Get updates from {profile?.username || 'this creator'}</p>
                    <p className="text-xs font-bold text-slate-500">Subscribe before you leave the profile.</p>
                  </div>
                  <button type="button" onClick={onSubscribe} className="rounded-xl px-4 py-3 text-xs font-black uppercase tracking-widest text-white" style={{ backgroundColor: accent }}>
                    Subscribe
                  </button>
                </div>
              )}
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-sm font-black text-slate-950">Create your own Linkit</p>
                <p className="mt-1 text-xs font-bold text-slate-500">Build a profile for links, products, subscribers, and analytics.</p>
                <a href="/login?signup=true" className="mt-3 inline-flex rounded-xl bg-slate-950 px-4 py-3 text-xs font-black uppercase tracking-widest text-white">
                  Get Started
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ============================================================
   PROFILE UTILITY SHEET
   ============================================================ */
const ProfileUtilitySheet = ({
  isOpen,
  onClose,
  profile,
  profileUrl,
  qrTheme,
  onSubscribe,
  onMessage,
  onShare,
  onReport,
}) => {
  const [claimName, setClaimName] = useState('');
  const [claimBloom, setClaimBloom] = useState(null);
  const [claimAvailability, setClaimAvailability] = useState('idle');
  const accent = normalizeHexColor(getProfileAccentColor(profile)) || '#0F172A';
  const ink = normalizeHexColor(profile?.title_color) || '#0F172A';
  const surface = normalizeHexColor(profile?.card_bg || profile?.bg_color) || '#FFFFFF';
  const displayName = profile?.full_name || `@${profile?.username || 'creator'}`;
  const handle = profile?.username ? `@${profile.username}` : 'Linkit profile';
  const claimTarget = claimName.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
  const claimStatusCopy = {
    idle: claimTarget ? 'Keep typing' : 'Enter a name to check',
    loading: 'Loading checker',
    verifying: 'Checking name',
    available: 'Available',
    taken: 'Already taken',
    error: 'Could not check',
  }[claimAvailability] || '';
  const canClaimName = !claimTarget || (claimTarget.length >= 3 && claimAvailability !== 'taken' && claimAvailability !== 'verifying' && claimAvailability !== 'loading');

  useEffect(() => {
    if (!isOpen || claimBloom || claimTarget.length < 3) return undefined;

    let cancelled = false;
    setClaimAvailability('loading');
    import('../lib/supabase')
      .then(({ fetchAllUsernames }) => fetchAllUsernames())
      .then((usernames) => {
        if (cancelled) return;
        const nextBloom = new BloomFilter();
        nextBloom.addAll(usernames || []);
        setClaimBloom(nextBloom);
      })
      .catch(() => {
        if (!cancelled) setClaimAvailability('error');
      });

    return () => {
      cancelled = true;
    };
  }, [claimBloom, claimTarget.length, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (!claimTarget) {
      setClaimAvailability('idle');
      return undefined;
    }
    if (claimTarget.length < 3) {
      setClaimAvailability('idle');
      return undefined;
    }
    if (!claimBloom) {
      setClaimAvailability('loading');
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      if (!claimBloom.check(claimTarget)) {
        setClaimAvailability('available');
        return;
      }

      setClaimAvailability('verifying');
      try {
        const supabase = await getSupabaseClient();
        const { data } = await supabase
          .from('profiles')
          .select('username')
          .eq('username', claimTarget)
          .maybeSingle();
        if (!cancelled) setClaimAvailability(data ? 'taken' : 'available');
      } catch {
        if (!cancelled) setClaimAvailability('error');
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [claimBloom, claimTarget, isOpen]);

  const copyProfile = async () => {
    if (!profileUrl) return;
    await navigator.clipboard?.writeText(profileUrl);
    toast.success('Profile link copied');
  };

  const claimProfile = () => {
    if (claimTarget && claimTarget.length < 3) {
      toast.error('Name must be at least 3 characters.');
      return;
    }
    if (claimAvailability === 'taken') {
      toast.error('That name is already taken.');
      return;
    }
    const query = claimTarget ? `?intent=${encodeURIComponent(claimTarget)}` : '?signup=true';
    window.location.href = `/login${query}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[590] flex items-end justify-center bg-slate-950/55 px-3 pb-3 pt-10 backdrop-blur-xl sm:items-center sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 28, scale: 0.98 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 28, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 420, damping: 34 }}
            onClick={(event) => event.stopPropagation()}
            className="profile-utility-sheet no-scrollbar w-full max-w-lg overflow-hidden rounded-[2rem] border shadow-2xl"
            style={{
              backgroundColor: surface,
              borderColor: hexToRgba(ink, 0.12),
              color: ink,
              boxShadow: `0 28px 90px -34px ${hexToRgba(accent, 0.65)}`,
            }}
          >
            <div className="flex items-start justify-between gap-4 p-5 sm:p-6">
              <div className="min-w-0">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl" style={{ backgroundColor: hexToRgba(accent, 0.14), color: accent }}>
                  <span className="text-sm font-black">L</span>
                </div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: hexToRgba(ink, 0.48) }}>Linkit Profile</p>
                <h3 className="mt-1 truncate text-2xl font-black tracking-tight">{displayName}</h3>
                <p className="mt-1 text-sm font-bold" style={{ color: hexToRgba(ink, 0.58) }}>{handle}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-black/5"
                aria-label="Close Linkit profile tools"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-3 px-5 pb-5 sm:grid-cols-2 sm:px-6">
              {profile?.show_notify_btn && (
                <button type="button" onClick={onSubscribe} className="flex min-h-[54px] items-center justify-center gap-2 rounded-2xl px-4 text-sm font-black" style={{ backgroundColor: accent, color: '#FFFFFF' }}>
                  <Bell size={17} /> Subscribe
                </button>
              )}
              {profile?.contact_email && (
                <button type="button" onClick={onMessage} className="flex min-h-[54px] items-center justify-center gap-2 rounded-2xl border px-4 text-sm font-black" style={{ borderColor: hexToRgba(ink, 0.14), backgroundColor: hexToRgba(accent, 0.08) }}>
                  <MessageCircle size={17} /> Message
                </button>
              )}
              <button type="button" onClick={onShare} className="flex min-h-[54px] items-center justify-center gap-2 rounded-2xl border px-4 text-sm font-black" style={{ borderColor: hexToRgba(ink, 0.14) }}>
                <Share2 size={17} /> Share
              </button>
              <button type="button" onClick={copyProfile} className="flex min-h-[54px] items-center justify-center gap-2 rounded-2xl border px-4 text-sm font-black" style={{ borderColor: hexToRgba(ink, 0.14) }}>
                <ExternalLink size={17} /> Copy Link
              </button>
            </div>

            <div className="grid gap-4 border-y px-5 py-5 sm:grid-cols-[auto_1fr] sm:px-6" style={{ borderColor: hexToRgba(ink, 0.1) }}>
              <div className="w-fit rounded-2xl bg-white p-2 shadow-inner ring-1 ring-slate-900/10">
                <QRCodeSVG
                  value={profileUrl || window.location.href}
                  size={118}
                  bgColor={qrTheme?.background || '#FFFFFF'}
                  fgColor={qrTheme?.foreground || '#111827'}
                  level="Q"
                  includeMargin
                />
              </div>
              <div className="flex min-w-0 flex-col justify-center">
                <div className="mb-3 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest" style={{ backgroundColor: hexToRgba(accent, 0.12), color: accent }}>
                    <ShieldCheck size={12} /> {profile?.is_verified && profile?.show_verified ? 'Verified' : 'Profile Tools'}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest" style={{ backgroundColor: hexToRgba(ink, 0.08), color: hexToRgba(ink, 0.66) }}>
                    <QrCode size={12} /> Mobile QR
                  </span>
                </div>
                <p className="text-sm font-bold leading-relaxed" style={{ color: hexToRgba(ink, 0.62) }}>
                  Open this creator profile on your phone, share it, or manage your subscriber access without leaving the page.
                </p>
              </div>
            </div>

            <div className="space-y-3 p-5 sm:p-6">
              <p className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: hexToRgba(ink, 0.45) }}>Create your own</p>
              <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                <div className="space-y-2">
                  <div className="flex min-h-[52px] items-center rounded-2xl border px-4 text-sm font-black" style={{ borderColor: hexToRgba(ink, 0.12), backgroundColor: hexToRgba(ink, 0.04) }}>
                    <span style={{ color: hexToRgba(ink, 0.44) }}>linkit.app/</span>
                    <input
                      value={claimName}
                      onChange={(event) => setClaimName(event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                      placeholder="yourname"
                      spellCheck={false}
                      autoCapitalize="none"
                      autoCorrect="off"
                      autoComplete="off"
                      className="min-w-0 flex-1 border-0 bg-transparent pl-1 outline-none ring-0 placeholder:opacity-40 focus:border-0 focus:outline-none focus:ring-0"
                      style={{ color: ink, caretColor: accent, boxShadow: 'none' }}
                    />
                  </div>
                  <p
                    className="text-[10px] font-black uppercase tracking-widest"
                    style={{
                      color: claimAvailability === 'available'
                        ? '#047857'
                        : claimAvailability === 'taken'
                          ? '#E11D48'
                          : hexToRgba(ink, 0.44),
                    }}
                  >
                    {claimStatusCopy}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={claimProfile}
                  disabled={!canClaimName}
                  className="min-h-[52px] rounded-2xl px-5 text-xs font-black uppercase tracking-widest text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-45"
                  style={{ backgroundColor: ink }}
                >
                  {claimAvailability === 'taken' ? 'Taken' : 'Claim'}
                </button>
              </div>
              <button type="button" onClick={onReport} className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest" style={{ color: hexToRgba(ink, 0.48) }}>
                <Flag size={13} /> Report this profile
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ============================================================
   LOCATION CONSENT PROMPT
   ============================================================ */
const LocationConsentPrompt = ({ profile, busy, onAccept, onDismiss }) => {
  const accent = normalizeHexColor(getProfileAccentColor(profile)) || '#0F172A';
  const creator = profile?.username ? `@${profile.username}` : 'this creator';

  return (
    <div className="fixed inset-x-3 bottom-4 z-[70] mx-auto max-w-md sm:inset-x-auto sm:right-5 sm:bottom-5">
      <div className="rounded-3xl border border-slate-200 bg-white/95 p-4 shadow-2xl backdrop-blur-xl">
        <div className="flex gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-white"
            style={{ backgroundColor: accent }}
          >
            <MapPin size={19} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-black text-slate-950">Share precise area?</p>
            <p className="mt-1 text-xs font-bold leading-relaxed text-slate-600">
              Help {creator} understand where real profile engagement comes from. This uses your browser location, not the rough ISP/server-routing estimate.
            </p>
            <p className="mt-2 text-[10px] font-black uppercase leading-relaxed tracking-widest text-slate-400">
              Browser permission required. Stores coordinates with an accuracy radius for analytics only.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={onAccept}
                disabled={busy}
                className="inline-flex flex-1 items-center justify-center rounded-2xl px-4 py-3 text-xs font-black uppercase tracking-widest text-white transition-all disabled:opacity-60"
                style={{ backgroundColor: accent }}
              >
                {busy ? 'Checking...' : 'Share location'}
              </button>
              <button
                type="button"
                onClick={onDismiss}
                disabled={busy}
                className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-600 transition-all hover:bg-slate-100 disabled:opacity-60"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            disabled={busy}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-60"
            aria-label="Dismiss location prompt"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   REPORT MODAL
   ============================================================ */
const ReportModal = ({ isOpen, onClose, profile }) => {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const hasReportTurnstile = Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);
  const reportAccent = normalizeHexColor(getProfileAccentColor(profile)) || '#0F172A';
  const reportInk = normalizeHexColor(profile?.title_color) || '#0F172A';
  const reportSurface = normalizeHexColor(profile?.card_bg || profile?.bg_color) || '#FFFFFF';
  const reportButton = normalizeHexColor(profile?.button_color || reportAccent) || reportAccent;
  const reportButtonText = normalizeHexColor(profile?.link_text_color) || '#FFFFFF';
  const reportFieldText = getDarkProfileFieldColor(profile);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    getSupabaseClient()
      .then((supabase) => supabase.auth.getUser())
      .then(({ data: { user } }) => {
        if (!cancelled && user) {
          setCurrentUser(user);
          setCustomEmail(user.email || '');
        }
      })
      .catch(() => { });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);

    try {
      const supabase = await getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token && hasReportTurnstile && !turnstileToken) {
        toast.error('Complete the bot check before submitting.');
        setSubmitting(false);
        return;
      }

      const res = await fetch('/api/safety?action=report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          target_profile_id: profile.id,
          reason: reason === 'Other' ? customReason : reason,
          reporter_id: currentUser?.id || null,
          reporter_email: customEmail || null,
          turnstile_token: turnstileToken || null
        })
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success('Report submitted. Our moderation team will review it.');
      } else {
        const data = await res.json().catch(() => ({}));
        setTurnstileToken(null);
        toast.error(data.error || data.details || 'Submission Failed. Please try again.');
      }
    } catch (err) {
      toast.error(`Connection Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[600] flex items-center justify-center px-4 backdrop-blur-xl"
          style={{ backgroundColor: hexToRgba(reportInk, 0.62) }}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
            className="w-full max-w-md rounded-[2.5rem] border-2 p-8 shadow-2xl space-y-6"
            style={{
              backgroundColor: reportSurface,
              borderColor: hexToRgba(reportInk, 0.9),
              boxShadow: `0 24px 80px -28px ${hexToRgba(reportAccent, 0.55)}`,
              color: reportInk,
            }}
          >
            {!submitted ? (
              <>
                <div className="space-y-2">
                  <h3 className="text-2xl font-black anim-misprint" style={{ color: reportInk }}>Report Profile</h3>
                  <p className="text-sm font-bold" style={{ color: hexToRgba(reportInk, 0.62) }}>Help us keep the community safe. Why are you reporting this profile?</p>
                </div>

                <div className="space-y-2">
                  {['Illegal Content', 'Harassment', 'Explicit Violence', 'Scam / Fraud', 'Other'].map(r => (
                    <button
                      key={r}
                      onClick={() => setReason(r)}
                      className={cn(
                        "w-full py-4 px-6 rounded-2xl text-left font-bold transition-all border-2 active:scale-95",
                        reason === r ? "text-white shadow-lg" : "hover:opacity-90"
                      )}
                      style={{
                        borderColor: reason === r ? reportInk : hexToRgba(reportInk, 0.12),
                        backgroundColor: reason === r ? reportButton : hexToRgba(reportAccent, 0.08),
                        color: reason === r ? reportButtonText : reportInk,
                      }}
                    >
                      {r}
                    </button>
                  ))}
                </div>

                {/* Optional Email for Public Visitors */}
                {!currentUser && (
                  <div className="space-y-2 pt-2">
                    <label className="text-[10px] font-black uppercase ml-1" style={{ color: hexToRgba(reportInk, 0.48) }}>Notify me of the outcome (Optional)</label>
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={customEmail}
                      onChange={(e) => setCustomEmail(e.target.value)}
                      className="w-full p-4 bg-white border-2 rounded-2xl text-sm font-medium transition-all outline-none placeholder:text-slate-400"
                      style={{ borderColor: hexToRgba(reportInk, 0.14), color: reportFieldText, caretColor: reportFieldText }}
                    />
                  </div>
                )}

                <AnimatePresence>
                  {reason === 'Other' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <textarea
                        placeholder="Please describe the violation..."
                        value={customReason}
                        onChange={(e) => setCustomReason(e.target.value)}
                        className="w-full p-4 bg-white border-2 rounded-2xl text-sm font-medium focus:ring-0 transition-all outline-none min-h-[100px] resize-none placeholder:text-slate-400"
                        style={{ borderColor: hexToRgba(reportInk, 0.14), color: reportFieldText, caretColor: reportFieldText }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {!currentUser && hasReportTurnstile && (
                  <Turnstile
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    onSuccess={setTurnstileToken}
                    onExpire={() => setTurnstileToken(null)}
                    onError={() => setTurnstileToken(null)}
                    options={{ theme: 'light', appearance: 'interaction-only' }}
                  />
                )}

                <div className="flex gap-3 pt-2">
                  <button onClick={onClose} className="flex-1 py-4 font-bold" style={{ color: hexToRgba(reportInk, 0.48) }}>Cancel</button>
                  <button
                    disabled={!reason || (reason === 'Other' && !customReason) || (!currentUser && hasReportTurnstile && !turnstileToken) || submitting}
                    onClick={handleSubmit}
                    className="flex-[2] py-4 rounded-2xl font-black uppercase tracking-widest disabled:opacity-50 shadow-lg active:scale-[0.98] active:translate-x-[1px] active:translate-y-[1px] transition-all"
                    style={{ backgroundColor: reportButton, color: reportButtonText }}
                  >
                    {submitting ? 'Submitting...' : 'Send Report'}
                  </button>
                </div>

                <p className="text-[10px] text-center uppercase font-black tracking-widest pt-2" style={{ color: hexToRgba(reportInk, 0.48) }}>
                  🛡️ Your identity is strictly confidential and will never be shared.
                </p>
              </>
            ) : (
              <div className="text-center space-y-4 py-6">
                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle size={32} />
                </div>
                <h3 className="text-xl font-black" style={{ color: reportInk }}>Thank You</h3>
                <p className="text-sm font-bold" style={{ color: hexToRgba(reportInk, 0.62) }}>Your report has been received and added to our moderation queue.</p>
                <button onClick={onClose} className="w-full py-4 rounded-2xl font-black" style={{ backgroundColor: reportButton, color: reportButtonText }}>Close</button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProfileView;
